import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import format from 'date-fns/format';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { Globals } from 'src/app/shared/globals/globals';
import { CalculateMarginsAndPricesPayload, ItemPrice } from 'src/app/shared/models/data/calculateMarginsAndPricesPayload.model';
import { EnquiryHeader, EnquiryHeaderItem } from 'src/app/shared/models/data/enquiry.model';
import { EnquiryTotalPriceAndMargin } from 'src/app/shared/models/data/enquiryTotalPriceAndMargin.model';
import { EnquiryTotalPriceAndMarginPayload } from 'src/app/shared/models/data/enquiryTotalPriceAndMarginPayload.model';
import { ExtraCost, ExtraCostParameter, ExtraCostParametersValues } from 'src/app/shared/models/data/extraCostParametersValues.model';
import { ItemsPricesFromXlsx } from 'src/app/shared/models/data/itemsPricesFromXlsx.model.';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { ResizableColumnsService } from 'src/app/shared/services/resize-columns/resizable-columns.service';
import { TeiasoftLoaderService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-loader.service';
import { GridColumn, GridConfiguration, ODataGridFilterMap } from 'src/app/shared/teiasoft-bucket/teiasoft-grid-odata/teiasoft-grid-odata.component';
import { SelectOption } from 'src/app/shared/teiasoft-bucket/teiasoft-select/teiasoft-select.component';
import { TTButton } from 'src/app/shared/teiasoft-bucket/teiasoft-toolbar/teiasoft-toolbar.component';
import { UtilsService } from 'src/app/shared/services/utils/utils.service';


interface Columns {
  items: string[],
  extraCosts: string[],
  costs: string[],
}

interface ItemColumnGroup {
  id: string;
  startColumn: string;
  endColumn: string;
}

interface ItemColumnGroupHeaderToggle {
  groupId: string;
  collapsed: boolean;
  hiddenCount: number;
  startColumn: string;
  endColumn: string;
}

@Component({
  selector: 'app-modal-editinplace-costs',
  templateUrl: './modal-editinplace-costs.component.html',
  styleUrls: ['./modal-editinplace-costs.component.scss']
})
export class ModalEditinplaceCostsComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly CLOSE_FLOATING_DROPDOWNS_EVENT = 'teiasoft-close-floating-dropdowns';

  @Input() isMobile: boolean = false;
  @Input() theme: string = 'silver';
  @Input() enquiryNo!: string;
  @Input() enquiryHeader!: EnquiryHeader;
  @Input() items: EnquiryHeaderItem[] = new Array();
  @Input() itemsPricesFromXlsx?: ItemsPricesFromXlsx = new ItemsPricesFromXlsx();
  @Input() itemVisibleColumns!: string[];
  @Input() itemCostsColumns!: string[];



  @Output() closeModal: EventEmitter<any> = new EventEmitter();
  @Output() onSave: EventEmitter<any> = new EventEmitter();

  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren('checkboxContainer') checkboxContainers!: QueryList<ElementRef>;


  title = 'edit-in-place-costs'; // this is necessary to store the grid configuration
  buttonsList: TTButton[] = new Array();
  groupsButtonsList: TTButton[] = new Array();
  filterMaps: ODataGridFilterMap[] = new Array();
  filtersValues: any;
  clearFilters: boolean = false;
  gridFilterable: boolean = false;
  resizingFlag = { isResizing: false };
  resizeSelector = {
    gridSelector: 'app-modal-editinplace-costs',
    tableSelector: '.table-container',
    headerSelector: '.header-row',
    rowSelector: '.data-row',
  }
  recalculateColumnsWidth: boolean = false;
  DEFAULT_COL_WIDTH = 150;

  lastSelectedIndex: number | null = null;
  gridConfiguration: GridConfiguration = new GridConfiguration();
  showIncreaseModal = false;
  focusedColumn: string | null = null;
  pendingChangesColumn?: string;
  showAddExtraCost: boolean = false;
  showItemDeliveryDates: boolean = false;
  showSettingsDialog: boolean = false;
  selectedDeliveryDatesItem?: EnquiryHeaderItem | null = null;
  subscriptions = new Subscription();
  extraCostParametersValues?: ExtraCostParametersValues;
  availableExtraCost?: ExtraCostParameter[] = new Array();
  uomOptionsList: { [key: number]: SelectOption[] } = {};
  uomOptionsMap: { [key: string]: SelectOption[] } = {};
  hasValidationIssues: boolean = false;
  checkboxColumnContainerWidth: number = 0;
  readonly quickShowIconClass = 'fal fa-plus';
  selectedItemColumnsForQuickHide: string[] = [];
  selectedItemColumnsLookup: Set<string> = new Set<string>();
  selectedItemColumnAnchor: string | null = null;
  itemColumnGroups: ItemColumnGroup[] = [];
  readonly columnGroupingOptionsKey = 'columnGrouping';
  quickShowBeforeColumnsByItemColumn: Map<string, string[]> = new Map<string, string[]>();
  quickShowBetweenColumnsByItemColumn: Map<string, string[]> = new Map<string, string[]>();
  quickShowAfterColumnsByItemColumn: Map<string, string[]> = new Map<string, string[]>();
  groupToggleBeforeByItemColumn: Map<string, ItemColumnGroupHeaderToggle> = new Map<string, ItemColumnGroupHeaderToggle>();
  groupToggleAfterByItemColumn: Map<string, ItemColumnGroupHeaderToggle> = new Map<string, ItemColumnGroupHeaderToggle>();
  itemHeaderLabelByColumn: Map<string, string> = new Map<string, string>();
  private readonly costHeaderUomByColumn: { [key: string]: string } = {
    initialprice: 'costsUom',
    previousrevisionprice: 'costsUom',
    fullcost: 'costsUom',
    extracosts: 'costsUom',
    minimumprice: 'costsUom',
    recommendedprice: 'costsUom',
    finalprice: 'costsUom',
    alloysurchargevalue: 'costsUom',
    energysurchargevalue: 'costsUom',
    actualfmamountperweight: 'costsUom',
    finalpriceperlength: 'finalPricePerLengthUom',
    totalprice: 'totalPriceUom'
  };
  stickyLeftByColumn: Map<string, number> = new Map<string, number>();
  frozenItemColumnsLookup: Set<string> = new Set<string>();
  columnWidthByName: Map<string, number> = new Map<string, number>();
  availableItemColumnsLookup: Set<string> = new Set<string>();
  availableItemColumnsOrdered: string[] = [];
  columns: Columns = {
    items: [],
    extraCosts: [],
    costs: []
  };
  editableItemColumns: string[] = ['extraLeadTime', 'deliveryNotes'];

  notEditableColumns: string[] = ["customerItemNo", "unitSystem", "material", "sms.description", "marketFamily.name", "customerDeliveryDate", "energySurcharge", "applicableNorms", "remark", "normGrades.normGradeCode", "gradeCustomerAnalysisLimitation.limitation",
    "gradeCustomerOtherLimitation.limitation", "rawMaterialGrade.rawMaterialGradeCode", "npsSchedule.npsDescription", "od", "odUom", "odTolerancePlus", "odToleranceMinus", "odToleranceUom", "id", "idUom", "idTolerancePlus", "idToleranceMinus", "idToleranceUom", "wt", "wtUom", "typeOfTolerance", "tubeExecution.description", "wtTolerancePlus", "wtToleranceMinus", "wtToleranceUom",
    "quantity", "quantityUom", "lengthType", "multipleLength", "lengthMin", "lengthMax", "lengthNominal", "lengthUom", "uBent", "totalWeight", "totalWeightUom", "mill.shortName", "surfaceType.name", "endsCondition", "testCondition", "sql.name", "offerScheme.offerSchemeDescription", "ut", "hydroTest", "ht", "htAdditional", "inspectionType3Dot2", "multipleCutting", "deliveryDate", "fullCost", "extraCosts", "minimumFm", "recommendedFm", "customerBonus", "minimumPrice", "recommendedPrice", "costsUom", "finalPricePerLength", 'alloySurchargeValue', 'energySurchargeValue', 'initialPrice', 'previousRevisionPrice', 'AS Extra Cost', "AS Extra Cost Uom"];
  numericColumns: string[] = ["od", "odTolerancePlus", "odToleranceMinus", "odToleranceUom", "id", "idTolerancePlus", "idToleranceMinus", "idToleranceUom", "wt", "wtTolerancePlus", "wtToleranceMinus", "quantity", "lengthMin", "lengthMax", "lengthNominal", "totalWeight", "extraLeadTime", "fullCost", "extraCosts", "minimumFm", "recommendedFm", "customerBonus", "minimumPrice", "recommendedPrice", "actualFmAmountPerWeight", "finalPricePerLength", 'alloySurchargeValue', 'energySurchargeValue', 'initialPrice', 'previousRevisionPrice', 'actualFm', 'negotiationMargin', 'agentFee', 'finalPrice'];
  selectColumns: string[] = [];
  booleanColumns: string[] = ["energySurcharge", "multipleLength", 'cts', 'ut',
    'hydroTest', 'ht', 'htAdditional', 'dyePenetrant', 'dyePenetrant100P', 'penalty', 'colorCoding', 'specialOperations', 'specialTests', 'grindingHoning', 'ec', 'notStdPackaging', 'inspectionType3Dot2', 'multipleCutting', 'uBent'];
  multiSelectColumns: string[] = ["normGrades.normGradeCode", "rawMaterialGrade.rawMaterialGradeCode", "unitSystem", "quantityUom", "lengthType", 'endsCondition', "typeOfTolerance", "tubeExecution.description", "mill.shortName", "surfaceType.name", "sms.description", "marketFamily.name", "totalWeightUom", "sql.name", "offerScheme.offerSchemeDescription"];
  activeFilters: { [key: string]: any } = {};
  activeMultiSelectionFilters: { [key: string]: any[] } = {};
  filteredItems: EnquiryHeaderItem[] = [];
  booleanColumnsOptions: SelectOption[] = [];
  enquiryTotals: EnquiryTotalPriceAndMargin | null = null;
  enquiryTotalPriceFormatted: string = '';
  enquiryTotalMarginFormatted: string = '';
  enquiryTotalMarginClass: string = '';
  private enquiryTotalsRequestId = 0;
  private tableResizeObserver?: ResizeObserver;
  private tableMutationObserver?: MutationObserver;
  private pendingLayoutSyncFrame: number | null = null;

  itemFieldsExtraCostsMap: any = {
    ut: "UT Extra Cost",
    hydroTest: "Hydro Extra Cost",
    ht: "Sep.HT Extra Cost",
    htAdditional: "Add. HT Extra Cost"
  }

  constructor(private backendService: BackendService, private cdr: ChangeDetectorRef, private globals: Globals, private teiasoftLoader: TeiasoftLoaderService, private translateService: TranslateService, private resizableColumnsService: ResizableColumnsService, private utils: UtilsService) {

  }


  ngOnInit(): void {
    if (this.itemsPricesFromXlsx) {
      this.items = this.itemsPricesFromXlsx.items!.map(i => i.item!);
      this.updateValidationStatus();
    }

    this.items = this.items.sort(this.sortByItemNo);

    this.filteredItems = [...this.items];
    this.getExtraCostsParameters().then(() => {
      this.populateUomOptions();
      this.populateBooleanOptions();
      this.generateColumns();
      this.rebuildAvailableItemColumnsCache();
      this.initGridConfiguration();
      this.buildButtonsList();
      this.buildFilters();
      this.refreshEnquiryTotals();
      this.teiasoftLoader.stop();
    });

    window.addEventListener('resize', this.onWindowResize);
  }

  ngAfterViewInit(): void {
    this.initTableLayoutObservers();
    this.scheduleLayoutSync();
  }

  calculateCheckboxContainerWidth() {
    setTimeout(() => {
      if (this.checkboxContainers.first) {
        this.checkboxColumnContainerWidth = this.checkboxContainers.first.nativeElement.offsetWidth;
        this.rebuildItemColumnCaches();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.resizableColumnsService.reset();
    if (this.tableResizeObserver) {
      this.tableResizeObserver.disconnect();
    }
    if (this.tableMutationObserver) {
      this.tableMutationObserver.disconnect();
    }
    if (this.pendingLayoutSyncFrame !== null) {
      window.cancelAnimationFrame(this.pendingLayoutSyncFrame);
      this.pendingLayoutSyncFrame = null;
    }
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize = (): void => {
    this.scheduleLayoutSync();
  };

  private initTableLayoutObservers(): void {
    const tableElement = this.tableContainer?.nativeElement;
    if (!tableElement) {
      return;
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.tableResizeObserver = new ResizeObserver(() => this.scheduleLayoutSync());
      this.tableResizeObserver.observe(tableElement);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.tableMutationObserver = new MutationObserver(() => this.scheduleLayoutSync());
      this.tableMutationObserver.observe(tableElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  private scheduleLayoutSync(): void {
    if (this.pendingLayoutSyncFrame !== null) {
      return;
    }

    this.pendingLayoutSyncFrame = window.requestAnimationFrame(() => {
      this.pendingLayoutSyncFrame = null;
      this.calculateCheckboxContainerWidth();
      this.refreshColumnsWidth();
    });
  }

  onTableContainerScroll(): void {
    document.dispatchEvent(new CustomEvent(ModalEditinplaceCostsComponent.CLOSE_FLOATING_DROPDOWNS_EVENT));
  }

  onSaveClick() {
    if (this.hasValidationIssues) {
      return;
    }

    this.onSave.emit(this.items);
  }

  onCloseModalClick() {
    this.closeModal.emit('');
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  doesColumnAllowNegative(column: string): boolean {
    if (column.toLowerCase() == 'finalprice' || column.toLowerCase() == 'agentfee' || column.toLowerCase() == 'negotiationmargin') {
      return false;
    }
    return true;
  }

  onAddExtraCostClick() {
    const alreadyApplied = this.getAlreadyAppliedExtraCosts();
    const canBeAppliedExtraCosts = this.extraCostParametersValues?.extraCosts?.filter(ex => !alreadyApplied?.includes(ex.extraCostId));
    this.showAddExtraCost = true;
    this.availableExtraCost = canBeAppliedExtraCosts;
  }

  /**
  * TOOLBAR
  */
  buildButtonsList() {
    this.buttonsList = new Array();
    let _item: TTButton = new TTButton();

    _item = new TTButton();
    _item.disabled = this.getSelectedItemColumnCount() < 1;
    _item.icon = 'fal fa-eye-slash';
    _item.id = 12;
    _item.size = "";
    _item.rawText = `Hide selected (${this.getSelectedItemColumnCount()})`;
    _item.rawTooltip = 'Hide selected columns and save as a group';
    _item.action = "hideSelectedRange";
    _item.type = "";
    _item.state = '';
    this.buttonsList.push(_item);

    /* SHOW/HIDE FILTER */
    _item = new TTButton();
    _item.disabled = false;
    _item.icon = !this.gridFilterable ? 'fal fa-filter' : 'fal fa-filter-circle-xmark';
    _item.id = 5;
    _item.size = "";
    _item.text = "";
    _item.tooltip = !this.gridFilterable ? "showGridFilters" : "hideGridFilters";
    _item.action = !this.gridFilterable ? "showFilter" : "closeFilter";
    _item.type = "";
    _item.state = '';
    _item.state = this.isDataFiltered() ? 'pressed' : '';
    this.buttonsList.push(_item);

    /* CLEAR FILTERS */
    _item = new TTButton();
    _item.disabled = !this.gridFilterable ? true : false;
    _item.icon = 'fal fa-eraser';
    _item.id = 8;
    _item.size = "";
    _item.text = "";
    _item.tooltip = "clearGridFilters";
    _item.action = "clearFilter";
    _item.type = "";
    _item.state = '';
    this.buttonsList.push(_item);

  }

  private updateHideSelectedButtonState(): void {
    const hideSelectedButton = this.buttonsList.find(b => b.action == 'hideSelectedRange');
    if (!hideSelectedButton) {
      this.buildButtonsList();
      return;
    }

    const selectedCount = this.getSelectedItemColumnCount();
    hideSelectedButton.disabled = selectedCount < 1;
    hideSelectedButton.rawText = `Hide selected (${selectedCount})`;
  }

  buildFilters(): void {

    this.filtersValues = {};
    this.filterMaps = new Array();

    this.filterMaps = this.multiSelectColumns.map(msc => {

      this.filtersValues[msc] = [...new Set(this.items.map(i => this.getInputValue(i, msc)))];
      let filter = new ODataGridFilterMap();
      filter.columnName = msc;
      filter.filterName = msc;
      return filter;

    });
  }

  isDataFiltered(): boolean {
    if (Object.keys(this.activeFilters).length > 0)
      return true;
    if (Object.keys(this.activeMultiSelectionFilters).length > 0)
      return true;
    return false;
  }

  onBtnToolbarClick(btn: TTButton) {
    if (btn.action == 'hideSelectedRange') {
      this.hideSelectedItemColumnsAsGroup();
      return;
    }

    if (btn.action && btn.action.startsWith('toggleGroup|')) {
      const groupId = btn.action.split('|')[1];
      this.toggleItemColumnGroupVisibility(groupId);
      return;
    }

    if (btn.action && btn.action.startsWith('removeGroup|')) {
      const groupId = btn.action.split('|')[1];
      this.removeItemColumnGroup(groupId);
      return;
    }

    /* SHOW FILTER */
    if (btn.action == 'showFilter') {
      this.gridFilterable = true;
      this.buildButtonsList();
    }

    /* CLOSE FILTER */
    if (btn.action == 'closeFilter') {
      this.gridFilterable = false;
      this.buildButtonsList();
    }

    if (btn.action == 'clearFilter') {
      this.activeFilters = {};
      this.activeMultiSelectionFilters = { ...{} };
      this.filtersValues = null;
      this.clearFilters = true;
      this.applyFilters();
      this.buildButtonsList();
      setTimeout(() => {
        this.clearFilters = false;
        this.activeMultiSelectionFilters = {};
      }, 0);
    }

  }

  onRowClick(item: any, index: number): void {

    const selection = this.getSelectedItems();

    if (selection.length == 0) {
      item.isSelected = true;
      return;
    }

    // if one row is selected and there are no pending changes => jump to the new row and deselect the current
    if (selection.length == 1 && !this.pendingChangesColumn) {
      selection[0]['isSelected'] = false
      this.onRowSelected(item, index);
      return;
    }
  }

  onRowSelected(item: any, index: number, event: MouseEvent | null = null): void {
    if (event)
      event.stopPropagation();

    const isShiftPressed = event && event.shiftKey;

    if (isShiftPressed && this.lastSelectedIndex !== null) {
      // Get the range of indexes to select
      const [start, end] = [this.lastSelectedIndex, index].sort((a, b) => a - b);

      // Get the selection state from the currently clicked checkbox
      const newSelectionState = !item.isSelected;

      // Apply selection state to all rows in the range (indexes refer to the visible/filtered list)
      for (let i = start; i <= end; i++) {
        this.filteredItems[i]['isSelected'] = newSelectionState;
        this.filteredItems[i]['isEditing'] = newSelectionState;
      }
    } else {
      // Normal selection behavior
      item.isSelected = !item.isSelected;
      item.isEditing = item.isSelected;
    }

    // Update last selected index
    this.lastSelectedIndex = index;
  }

  onSelectAll($event: any) {
    const checkbox = $event.target as HTMLInputElement;
    const targetItems = this.filteredItems;
    const selected = this.getSelectedItems(targetItems);

    if (selected.length === 0) {
      // SELECT ALL
      targetItems.forEach(i => i['isSelected'] = true);
    } else {
      // REMOVE ALL SELECTION (even if only some are selected)
      targetItems.forEach(i => i['isSelected'] = false);
      checkbox.checked = false;
    }

    checkbox.indeterminate = false;
  }


  onAddExtraCostsSave($event: any) {
    const newIds = $event as [];
    newIds.forEach(i => this.generateExtraCostColumn(i));
    this.refreshColumnsWidth();
    this.showAddExtraCost = false;
  }

  onCheckboxChange(item: any, index: number, event: Event): void {
    event.stopPropagation();  // Prevents the click event on the div from being triggered
    if (item.isSelected) {
      item.isEditing = true;
    } else {
      item.isEditing = false;
    }
  }

  capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  getColumnHeaderLabel(colName?: string) {
    if (colName) {
      const cached = this.itemHeaderLabelByColumn.get(colName.toLowerCase());
      if (cached) return cached;
    }

    const uomColumnsMapping: any = {
      od: "odUom",
      id: "idUom",
      wt: "wtUom",
      lengthNominal: "lengthUom",
      lengthMin: "lengthUom",
      lengthMax: "lengthUom"
    };

    if (colName && uomColumnsMapping[colName]) {
      const uomColumn = uomColumnsMapping[colName];
      const baseLabel = this.translateService.instant('grid.' + this.capitalizeFirstLetter(colName));
      const uomValue = this.items.find(item => item[uomColumn])?.[uomColumn] || "";

      return uomValue ? `${baseLabel} (${uomValue})` : baseLabel;

    }

    if (this.columns.extraCosts.find(ec => ec === colName))
      return colName;

    const computed = this.translateService.instant('grid.' + this.capitalizeFirstLetter(colName!));
    if (colName) {
      this.itemHeaderLabelByColumn.set(colName.toLowerCase(), computed);
    }
    return computed;
  }

  getConfirmableInputClass(columnName: string, row: EnquiryHeaderItem): string | string[] {
    if (columnName.toLowerCase() == 'alloysurchargevalue' && this.isAlloySurchargeWarning(row?.['alloySurchargeValue'])) {
      return 'warning-border';
    }

    if (columnName.toLowerCase() === 'extraleadtime' && this.hasInvalidResultingDeliveryWeek(row)) {
      return 'border-red';
    }

    if (columnName.toLowerCase() == 'finalprice') {
      if (((row.finalPrice && row.minimumPrice) && (+row.finalPrice < +row.minimumPrice)) || ((row.finalPrice && row.recommendedPrice && +row.recommendedFm! == 0 && (+row.recommendedPrice == +row.finalPrice))))
        return 'bg-red';
      if ((row.finalPrice && row.minimumPrice && row.recommendedPrice) && (+row.finalPrice >= +row.minimumPrice && +row.finalPrice < +row.recommendedPrice))
        return 'bg-yellow';
      if ((row.finalPrice && row.minimumPrice && row.recommendedPrice) && (+row.finalPrice >= +row.recommendedPrice))
        return 'bg-green';
    }
    return '';
  }

  private isAlloySurchargeWarning(value: any): boolean {
    return value !== null && value !== undefined && value !== '' && Number(value) === 0;
  }

  getAlreadyAppliedExtraCosts() {
    const extraCostCols = this.columns.extraCosts.filter(col => !col.includes('Uom')); // ignore uom columns
    const extraCostIds = this.extraCostParametersValues?.extraCosts?.filter(ex => extraCostCols.includes(ex.extraCostName!)).map(ex => ex.extraCostId);
    return extraCostIds;
  }

  sortByItemNo(field1: any, field2: any): number {
    const itemNo1 = field1.itemNo ? field1.itemNo : '';
    const itemNo2 = field2.itemNo ? field2.itemNo : '';

    const numPart1 = itemNo1.match(/\d+/g);
    const numPart2 = itemNo2.match(/\d+/g);

    if (numPart1 && numPart2) {
      const num1 = parseInt(numPart1[0], 10);
      const num2 = parseInt(numPart2[0], 10);

      // Compare the numeric parts
      return num1 - num2;
    }

    return itemNo1.localeCompare(itemNo2);
  }


  getInputValue(item: any, col: string): string | number | boolean {
    if (this.isExtraCostAmountColumn(col)) {
      const extraCost = item.extraCostsCollection.find((extraCost: any) =>
        this.extraCostMatchesColumn(col, extraCost)
      );
      return extraCost && extraCost.extraCostAmount ? Number(extraCost.extraCostAmount).toFixed(2) : '';
    }

    if (this.isExtraCostUomColumn(col, item)) {
      const extraCost = item.extraCostsCollection.find((extraCost: any) =>
        this.extraCostMatchesColumn(col, extraCost, true)
      );
      return extraCost ? extraCost.extraCostUom : '';
    }

    if (col.endsWith("N. of Tests")) {
      const baseColumn = col.replace(" N. of Tests", "");
      const extraCost = item.extraCostsCollection.find((extraCost: any) =>
        this.extraCostMatchesColumn(baseColumn, extraCost)
      );

      return extraCost && extraCost.numberOfTests ? Number(extraCost.numberOfTests) : (this.isPerTestUom(extraCost?.extraCostUom) ? 0 : '');
    }

    // Resolve nested properties using dot notation
    const value = col.split('.').reduce((acc, key) => {
      if (Array.isArray(acc)) {
        return acc.map((element) => element?.[key]).filter(Boolean).join(', ');
      }
      return acc?.[key];
    }, item);

    if (col === 'deliveryDate') {
      return this.getItemRowMinDeliveryDate(item);
    }

    // Handle arrays by joining values
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'object' ? JSON.stringify(v) : v)).join(', ');
    }

    if (col === 'extraLeadTime') {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return Number(value);
    }

    // Format numbers and booleans
    if (typeof value === 'number' && !this.isDateColumn(col)) {
      return value.toFixed(2);
    }
    if (typeof value === 'number' && this.isDateColumn(col)) {
      return format(new Date(value), this.globals.dateFormat!);
    }
    if (typeof value === 'boolean') {
      return this.getBooleanValue(value);
    }

    return value || '';
  }

  isDateColumn(column: string): boolean {
    return ['customerDeliveryDate', 'deliveryDate'].includes(column);
  }

  getCostHeaderLabel(col: string): string {
    const normalizedColumn = col.toLowerCase();
    const baseLabel = this.getTranslatedHeaderLabelWithoutUom(col);
    const uomColumn = this.costHeaderUomByColumn[normalizedColumn];
    const uomValue = this.getCostHeaderUom(uomColumn);
    return uomValue ? `${baseLabel} (${uomValue})` : baseLabel;
  }

  private getCostHeaderUom(uomColumn?: string): string {
    if (!uomColumn) {
      return '';
    }

    return (this.items.find(item => !!(item as any)?.[uomColumn]) as any)?.[uomColumn] ?? '';
  }

  private getTranslatedHeaderLabelWithoutUom(column: string): string {
    const translatedLabel = this.translateService.instant('grid.' + this.capitalizeFirstLetter(column));
    return this.stripHardcodedUomSuffix(translatedLabel);
  }

  private stripHardcodedUomSuffix(label: string): string {
    return (label || '').replace(/\s*\(?[^\s()]+\/[^\s()]+\)?\s*$/, '').trim();
  }


  getBooleanValue(val: boolean) {
    return val ? this.translateService.instant("noTranslations.yes") : this.translateService.instant("noTranslations.no");
  }

  getInputType(col: string): 'select' | 'input' | 'date' | 'inputnumeric' {
    if (col.includes('Uom')) return 'select';
    if (col === 'deliveryNotes') return 'input';
    return 'inputnumeric';
  }

  isEditableItemColumn(col: string, item: EnquiryHeaderItem): boolean {
    if (!this.editableItemColumns.includes(col)) {
      return false;
    }
    return this.isEditableColumn(col, item);
  }

  getSelectedItems(items: EnquiryHeaderItem[] = this.items): EnquiryHeaderItem[] {
    return items.filter(i => i['isSelected']);
  }

  async onConfirmChange(value: any, item: EnquiryHeaderItem, col: string): Promise<void> {
    if (!this.hasValueChanged(item, col, value)) {
      this.pendingChangesColumn = undefined;
      return;
    }

    const shouldRefreshTotals = this.shouldRefreshEnquiryTotals(col);
    const selectedItems = this.getSelectedItems();
    const prevSelectionItemNo = _.cloneDeep(selectedItems).map(i => i.itemNo);

    for (const i of selectedItems) {
      this.unselectItem(i);
      await this.updateItemColumn(i, col, value);  // wait for each update
    }

    this.pendingChangesColumn = undefined;
    // recover selection only after all updates are complete
    for (const i of this.items) {
      if (prevSelectionItemNo.includes(i.itemNo)) {
        this.selectItem(i);
      }
    }

    if (shouldRefreshTotals) {
      await this.refreshEnquiryTotals();
    }
  }

  private hasValueChanged(item: EnquiryHeaderItem, col: string, newValue: any): boolean {
    const currentValue = this.getInputValue(item, col);
    if (col === 'deliveryNotes') {
      return (currentValue ?? '') !== (newValue ?? '');
    }
    return this.normalizeValueForComparison(currentValue) !== this.normalizeValueForComparison(newValue);
  }

  private normalizeValueForComparison(value: any): string | number | boolean {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return '';
      }

      const numeric = Number(trimmed.replace(',', '.'));
      return Number.isNaN(numeric) ? trimmed : numeric;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? Number(value) : value;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '';
      }
    }

    return '';
  }

  private shouldRefreshEnquiryTotals(column: string): boolean {
    if (!column) return false;
    if (column === 'deliveryNotes' || column === 'extraLeadTime') return false;
    return true;
  }


  async updateItemColumn(item: any, col: string, newValue: any): Promise<void> {
    if (col === 'agentFee' && !this.isAgentFeeEditable(item)) {
      return;
    }

    // Handle N. of Tests column for per-test extra cost UOMs
    if (col.endsWith("N. of Tests")) {
      const baseColumn = col.replace(" N. of Tests", "");
      const extraCost = item.extraCostsCollection.find((extraCost: any) =>
        this.extraCostMatchesColumn(baseColumn, extraCost)
      );

      if (extraCost) {
        extraCost.numberOfTests = newValue; // Store the N. of Tests
      } else {
        const ex = this.extraCostParametersValues?.extraCosts?.find(
          ec => ec.extraCostName === baseColumn
        );
        if (ex) {
          item.extraCostsCollection.push({
            extraCostAmount: 0,
            extraCostId: ex.extraCostId,
            extraCostUom: this.getPerTestUom(item, ex),
            numberOfTests: newValue
          });
        }
      }
    }
    // If the column is related to an extra cost amount
    else if (this.isExtraCostAmountColumn(col)) {
      if (!item.extraCostsCollection) {
        item.extraCostsCollection = [];
      }
      const extraCost = item.extraCostsCollection.find((extraCost: any) =>
        this.extraCostMatchesColumn(col, extraCost)
      );
      if (extraCost) {
        extraCost.extraCostAmount = newValue || 0; // Update the amount
        if (!extraCost.extraCostUom) {
          const ex = this.extraCostParametersValues?.extraCosts?.find(
            ec => ec.extraCostName === col
          );
          extraCost.extraCostUom = item.unitSystem === 'Metric' ? ex?.extraCostMetricUoms![0] : ex?.extraCostImperialUoms![0];
        }
      } else {
        const ex = this.extraCostParametersValues?.extraCosts?.find(
          ec => ec.extraCostName === col
        );

        item.extraCostsCollection.push({
          extraCostAmount: newValue || 0,
          extraCostId: ex?.extraCostId,
          extraCostUom:
            item.unitSystem === 'Metric'
              ? ex?.extraCostMetricUoms![0]
              : ex?.extraCostImperialUoms![0],
        });
      }
      this.removeValidationIssueExtraCost(item, col, newValue);
      await this.calculateItemMarginsAndPrices(item, 'ItemExtraCosts');
    }
    // If the column is related to a UOM
    else if (col.includes('Uom')) {
      if (!item.extraCostsCollection) {
        item.extraCostsCollection = [];
      }
      const extraCost = item.extraCostsCollection.find((extraCost: any) =>
        this.extraCostMatchesColumn(col, extraCost, true)
      );
      if (extraCost) {
        extraCost.extraCostUom = newValue; // Update the UOM
      } else {
        const ex = this.extraCostParametersValues?.extraCosts?.find(
          ec => `${ec.extraCostName} Uom` === col
        );

        item.extraCostsCollection.push({
          extraCostAmount: 0,
          extraCostId: ex?.extraCostId,
          extraCostUom: newValue,
        });
      }
      this.removeValidationIssue(item, col, newValue);
      await this.calculateItemMarginsAndPrices(item, 'ItemExtraCosts');

    }
    // Handle specific columns like 'actualFm', 'actualFmAmountPerWeight', 'agentFee', 'negotiationMargin'
    else if (['actualFm', 'actualFmAmountPerWeight', 'agentFee', 'negotiationMargin'].includes(col)) {
      item[col] = newValue;
      item.isActualCmManuallyChanged = col === 'actualFm' || col === 'actualFmAmountPerWeight';
      item.isFinalPriceManuallyChanged = false;

      if (col === 'actualFm')
        await this.calculateItemMarginsAndPrices(item, 'ActualFm');
      if (col === 'actualFmAmountPerWeight')
        await this.calculateItemMarginsAndPrices(item, 'ActualFmAmountPerWeight');
      if (col === 'agentFee')
        await this.calculateItemMarginsAndPrices(item, 'AgentFee');
      if (col === 'negotiationMargin')
        await this.calculateItemMarginsAndPrices(item, 'NegotiationMargin');

      this.removeValidationIssue(item, col, newValue);
    }
    // Handle 'finalPrice' column
    else if (col === 'finalPrice') {
      item['previousFinalPrice'] = Number(item[col]);
      item[col] = newValue;
      item.isFinalPriceManuallyChanged = true;
      item.isActualCmManuallyChanged = false;
      await this.calculateItemMarginsAndPrices(item, 'FinalPrice');

      this.removeValidationIssue(item, col, newValue);
      this.removeValidationIssue(item, 'actualFm', item.actualFm);
    }
    // For regular columns
    else {
      if (col === 'extraLeadTime') {
        item[col] = this.toRoundedInteger(newValue);
        this.updateDeliveryDatesWithExtraLeadTime(item, item[col]);
      } else if (col === 'deliveryNotes') {
        item[col] = newValue ?? '';
      } else {
        item[col] = newValue;
      }
      this.removeValidationIssue(item, col, newValue);
    }
  }

  private toRoundedInteger(value: any): number {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return 0;
    return Math.round(parsedValue);
  }

  private updateDeliveryDatesWithExtraLeadTime(item: EnquiryHeaderItem, extraLeadTime?: number): void {
    if (!item) return;

    const deliverySource = (item.deliverySource || '').trim().toLowerCase();
    const normalizedExtraLeadTime = this.toRoundedInteger(extraLeadTime ?? item.extraLeadTime ?? 0);

    if (deliverySource === 'itdcr') {
      return;
    }

    if (deliverySource === 'delegation' || deliverySource === 'manual') {
      const deliveryDates = Array.isArray(item.deliveryDatesCollection) ? item.deliveryDatesCollection : [];
      deliveryDates.forEach(dd => {
        const baseWeeks = this.toRoundedInteger(dd.deliveryNumberOfWeeks ?? 0);
        dd.deliveryNumberOfWeeksWithExtraLeadTime = baseWeeks + normalizedExtraLeadTime;
      });
      item.deliveryDatesCollection = deliveryDates;
      return;
    }

    if (deliverySource === 'none') {
      item.deliveryDatesCollection = [{
        deliveryNumberOfWeeks: 0,
        deliveryNumberOfWeeksWithExtraLeadTime: normalizedExtraLeadTime
      }];
    }
  }


  calculateItemActualCm(i: EnquiryHeaderItem): number | undefined {
    return Number((Number(i.finalPrice) - (Number(i.fullCost) + Number(i.extraCosts))).toFixed(2));
  }

  calculateExtraCostSummary(items: EnquiryHeaderItem[]): Promise<void[]> {
    const promises = items.map(i => {
      return this.calculateItemMarginsAndPrices(i, 'ItemExtraCosts');
    })
    return Promise.all(promises);
  }

  safeToFixed(value: any, decimals: number = 2): number {
    const num = Number(value);
    return isNaN(num) ? 0 : Number(num.toFixed(decimals));
  }

  calculateItemMarginsAndPrices(item: EnquiryHeaderItem, field: "ActualFm" | "ActualFmAmountPerWeight" | "NegotiationMargin" | "AgentFee" | "FinalPrice" | "ItemExtraCosts"): Promise<void> {
    const payload = new CalculateMarginsAndPricesPayload();
    payload.itemPrice = new ItemPrice();
    payload.changedField = field;
    payload.previousFinalPrice = field === 'FinalPrice' ? Number(item['previousFinalPrice']?.toFixed(2)) : undefined;
    payload.itemPrice.actualFm = this.safeToFixed(item.actualFm);
    payload.itemPrice.actualFmAmountPerWeight = this.safeToFixed(item.actualFmAmountPerWeight);
    payload.itemPrice.agentFee = this.safeToFixed(item.agentFee);
    payload.itemPrice.negotiationMargin = this.safeToFixed(item.negotiationMargin);
    payload.itemPrice.finalPrice = this.safeToFixed(item.finalPrice);
    payload.itemPrice.itemExtraCosts = item?.extraCostsCollection?.filter(x => x.extraCostAmount && x.extraCostAmount > 0) || [];
    payload.itemPrice.orderExtraCost = this.enquiryHeader?.orderExtraCost;

    return new Promise((resolve) => {
      this.backendService.calculateMarginsAndPrices(this.enquiryHeader.enquiryNo!, item.itemNo!, payload)
        .subscribe(res => {
          if (res) {
            item.actualFm = res.actualFm;
            item.actualFmAmountPerWeight = res.actualFmAmountPerWeight;
            item.agentFee = res.agentFee;
            item.negotiationMargin = res.negotiationMargin;
            item.finalPrice = res.finalPrice;
            item.extraCosts = res.extraCosts;
            item.minimumPrice = res.minimumPrice;
            item.recommendedPrice = res.recommendedPrice;
            item.finalPricePerLength = res.finalPricePerLength;

            this.removeValidationIssue(item, 'actualFm', item.actualFm);
            this.removeValidationIssue(item, 'actualFmAmountPerWeight', item.actualFmAmountPerWeight);
            this.removeValidationIssue(item, 'finalPrice', item.finalPrice);
            this.removeValidationIssue(item, 'finalPricePerLength', item.finalPricePerLength);
            this.removeValidationIssue(item, 'agentFee', item.agentFee);
            this.removeValidationIssue(item, 'negotiationMargin', item.negotiationMargin);

          }
          resolve();
        });
    });
  }

  calculateItemFinalPrice(item: EnquiryHeaderItem) {
    const baseCost = Number(item.fullCost) + Number(item.extraCosts) + Number(item.actualFm);
    const priceWithNegotiation = baseCost + (baseCost * (item.negotiationMargin! / 100));
    const finalPrice = priceWithNegotiation / (1 - (item.agentFee! / 100));

    item.finalPrice = Number.isFinite(finalPrice) ? Number(finalPrice.toFixed(2)) : Number();

    this.removeValidationIssue(item, 'finalPrice', item.finalPrice);
  }

  getItemRowMinDeliveryDate(row: EnquiryHeaderItem): string {
    const item = this.items?.find(i => i.itemNo === row.itemNo) as EnquiryHeaderItem;
    if (!item) return '';

    if (item.deliverySource?.toLowerCase() === 'itdcr') {
      const minWeekOfYear = item.deliveryDatesCollection
        ?.map(dd => this.normalizeWeekOfYear(dd.deliveryWeekOfYearWithExtraLeadTime || dd.deliveryWeekOfYear))
        .filter((week): week is string => !!week)
        .reduce<string | undefined>((min, current) => {
          if (!min) return current;
          return this.isWeekOfYearBefore(current, min) ? current : min;
        }, undefined);

      if (minWeekOfYear) return minWeekOfYear;
    }

    const minWeeks = item?.deliveryDatesCollection
      ?.map(dd => dd.deliveryNumberOfWeeksWithExtraLeadTime)
      .filter(weeks => typeof weeks === 'number' && weeks > 0)
      .reduce((min, current) => Math.min(min!, current!), Infinity);

    return minWeeks === Infinity ? '' : this.formatWeeks(minWeeks!);
  }

  getItemRowDeliveryDatesCollection(row: EnquiryHeaderItem): any[] {
    const item = this.items?.find(i => i.itemNo == row.itemNo) as EnquiryHeaderItem;
    return item?.deliveryDatesCollection || [];
  }

  onItemDeliveryDatesClick(item: EnquiryHeaderItem): void {
    this.selectedDeliveryDatesItem = this.items?.find(i => i.itemNo == item.itemNo) || item;
    this.showItemDeliveryDates = true;
  }

  private normalizeWeekOfYear(value?: string): string | undefined {
    if (!value) return undefined;

    const trimmed = value.trim();
    if (!trimmed) return undefined;

    if (/^W\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const isoMatch = /^(\d{4})-W(\d{1,2})$/.exec(trimmed);
    if (isoMatch) {
      const weekNumber = parseInt(isoMatch[2], 10);
      const year = isoMatch[1].slice(-2);
      const paddedWeek = weekNumber.toString().padStart(2, '0');
      return `W${paddedWeek}-${year}`;
    }

    const compactMatch = /^W?(\d{1,2})[-/](\d{2,4})$/.exec(trimmed);
    if (compactMatch) {
      const weekNumber = parseInt(compactMatch[1], 10);
      const yearPart = compactMatch[2];
      const year = yearPart.length === 4 ? yearPart.slice(-2) : yearPart;
      const paddedWeek = weekNumber.toString().padStart(2, '0');
      return `W${paddedWeek}-${year}`;
    }

    return undefined;
  }

  private weekOfYearToComparable(value?: string): number | null {
    const normalized = this.normalizeWeekOfYear(value);
    if (!normalized) return null;

    const match = /^W(\d{2})-(\d{2})$/.exec(normalized);
    if (!match) return null;

    const week = Number(match[1]);
    const year = Number(match[2]);
    if (isNaN(week) || isNaN(year)) return null;

    return year * 100 + week;
  }

  private isWeekOfYearBefore(current: string, compareTo: string): boolean {
    const currentValue = this.weekOfYearToComparable(current);
    const compareValue = this.weekOfYearToComparable(compareTo);

    if (currentValue === null) return false;
    if (compareValue === null) return true;

    return currentValue < compareValue;
  }

  private formatWeeks(weeks: number): string {
    return `${weeks} week${weeks > 1 ? 's' : ''}`;
  }

  updateFinalPriceAndActualCmOnExtraCostUpdate(item: EnquiryHeaderItem) {
    // if the final price was manually changed we should update the actual CM 
    if (item.isFinalPriceManuallyChanged!!) {
      item.actualFm = this.calculateItemActualCm(item);
    }
    else {
      this.calculateItemFinalPrice(item);
    }
  }

  unselectItem(item: any): void {
    item['isSelected'] = false;
  }

  selectItem(item: any): void {
    item['isSelected'] = true;
  }

  temporarilyUnselectItems(): void {
    this.teiasoftLoader.start();
    this.items.forEach(i => i['wasSelected'] = i['isSelected']);

    // Set isSelected to false for all items
    this.items.forEach(i => i['isSelected'] = false);

    // Recover the selection (0ms ensures it's executed after all changes are applied)
    setTimeout(() => {
      this.items.forEach(i => {
        if (i['wasSelected']) {
          i['isSelected'] = true;
        }
      });
      this.pendingChangesColumn = undefined;
      this.teiasoftLoader.stop();
    }, 250);
  }

  onValueChanged(hasChanges: boolean, item: EnquiryHeaderItem, col: string): void {
    if (hasChanges)
      this.pendingChangesColumn = col;
    else
      this.pendingChangesColumn = undefined;
  }

  isEditableColumn(col: string, item: EnquiryHeaderItem): boolean {
    // Check if the column is in the non-editable list
    if (this.notEditableColumns.includes(col)) {
      return false;
    }

    // if there is a pending changing on another col, edit will be disabled
    if (this.pendingChangesColumn && this.pendingChangesColumn != col) {
      return false;
    }

    // special uom reserved for extra cost based on order
    if (col === "Order Extra Cost" || col === 'Order Extra Cost Uom') {
      return false;
    }

    // Check for UOM selection affecting N. of Tests column
    if (col.endsWith("N. of Tests")) {
      const baseColumn = col.replace(" N. of Tests", "");
      const extraCost = item.extraCostsCollection?.find((extraCost: any) =>
        this.extraCostMatchesColumn(baseColumn, extraCost)
      );
      return this.isPerTestUom(extraCost?.extraCostUom);
    }

    if (col === 'agentFee' && !this.isAgentFeeEditable(item)) {
      return false;
    }

    if (col.includes('Uom')) {
      const uomOptions = this.getUomOptionsBySystem(col, item);
      return uomOptions.length > 0;
    }

    // If it's an extra cost amount column, check the corresponding UOM column
    if (this.isExtraCostAmountColumn(col)) {
      // Find the related UOM column for this extra cost
      const uomColumn = `${col} Uom`; // Assuming the UOM column follows the same naming convention
      const uomOptions = this.getUomOptionsBySystem(uomColumn, item);
      return uomOptions.length > 0; // Block edit if no UOM options
    }



    return true; // Default to true for other editable columns
  }

  isAgentFeeEditable(item: EnquiryHeaderItem): boolean {
    return !Number.isFinite(item?.customerBonus);
  }

  private isPerTestUom(uom?: string | null): boolean {
    return (uom ?? '').toString().trim().toLowerCase().endsWith('/test');
  }

  private getPerTestUom(item: EnquiryHeaderItem, extraCost: any): string {
    const uoms = item.unitSystem === 'Metric' ? extraCost?.extraCostMetricUoms : extraCost?.extraCostImperialUoms;
    return uoms?.find((uom: string) => this.isPerTestUom(uom)) ?? '';
  }

  flattenProperties = (
    obj: Record<string, any>,
    parentKey = '',
    result: Record<string, any> = {}
  ): Record<string, any> => {
    for (const key of Object.keys(obj)) {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      const value = obj[key];

      if (Array.isArray(value)) {
        value.forEach((element, index) => {
          const arrayKey = `${newKey}`;
          if (typeof element === 'object' && element !== null) {
            this.flattenProperties(element, arrayKey, result);
          } else {
            result[arrayKey] = element;
          }
        });
      } else if (typeof value === 'object' && value !== null) {
        this.flattenProperties(value, newKey, result);
      } else {
        result[newKey] = value;
      }
    }
    return result;
  };

  private dedupeColumns(columns: string[]): string[] {
    const seen = new Set<string>();
    return columns.filter(col => {
      if (seen.has(col)) {
        return false;
      }
      seen.add(col);
      return true;
    });
  }

  private getEffectiveItemCostsColumns(): string[] {
    return this.dedupeColumns([...(this.itemCostsColumns ?? [])]);
  }

  private getEffectiveItemVisibleColumns(): string[] {
    const costsColumnsLookup = new Set(this.getEffectiveItemCostsColumns().map(col => col.toLowerCase()));
    return this.dedupeColumns(
      [...(this.itemVisibleColumns ?? [])].filter(col => !costsColumnsLookup.has(col.toLowerCase()))
    );
  }

  generateColumns(): void {
    if (this.items.length > 0) {
      // Flatten first item and collect keys
      const firstItem = this.flattenProperties(this.items[0]);
      const effectiveItemColumns = this.getEffectiveItemVisibleColumns();
      const effectiveCostColumns = this.getEffectiveItemCostsColumns();

      // Preserve the configured order while falling back to runtime keys
      const runtimeItemKeys = Object.keys(firstItem).filter((key) =>
        effectiveItemColumns.includes(key)
      );
      const runtimeCostKeys = Object.keys(firstItem).filter((key) =>
        effectiveCostColumns.includes(key)
      );

      this.columns.items = this.dedupeColumns([...effectiveItemColumns, ...runtimeItemKeys]);
      this.columns.costs = this.dedupeColumns([...effectiveCostColumns, ...runtimeCostKeys]);

      // Handle extra costs
      this.items.forEach((item) => {
        if (item.extraCostsCollection && item.extraCostsCollection.length > 0) {
          item.extraCostsCollection.forEach((extraCost) => {
            this.generateExtraCostColumn(extraCost.extraCostId!);
          });
        }
        else {
          this.addTransportExtraCost();
        }
      });
    }
  }


  generateExtraCostColumn(extraCostId: number) {
    const ec = this.extraCostParametersValues!.extraCosts!.find(e => e.extraCostId == extraCostId);
    if (ec) {
      const amountColumn = `${ec.extraCostName}`;
      const uomColumn = `${ec.extraCostName} Uom`;

      // Add extra cost columns if they are not already in the column list
      if (!this.columns.extraCosts.includes(amountColumn)) {
        this.columns.extraCosts.push(amountColumn);
      }
      if (!this.columns.extraCosts.includes(uomColumn)) {
        this.columns.extraCosts.push(uomColumn);
      }

      if (ec.extraCostMetricUoms?.some(uom => this.isPerTestUom(uom)) || ec.extraCostImperialUoms?.some(uom => this.isPerTestUom(uom))) {
        const testColumn = `${ec.extraCostName} N. of Tests`;
        if (!this.columns.extraCosts.includes(testColumn)) {
          this.columns.extraCosts.push(testColumn);
        }
      }
    }

    // add transport as default columns if missing
    this.addTransportExtraCost();
  }

  addTransportExtraCost() {
    const transport = this.extraCostParametersValues!.extraCosts!.find(e => e.extraCostName == 'Transport Extra Cost');
    if (transport && this.columns.extraCosts.findIndex(ec => ec == transport?.extraCostName) == -1) {
      const amountColumn = `${transport.extraCostName}`;
      const uomColumn = `${transport.extraCostName} Uom`;
      this.columns.extraCosts.push(amountColumn);
      this.columns.extraCosts.push(uomColumn);
    }
  }

  getExtraCostsParameters() {
    return new Promise((resolve, reject) => {
      this.backendService.getExtraCostsParametersValues(this.enquiryHeader?.currency).subscribe((res) => {
        this.extraCostParametersValues = res;
        resolve(res);
      }, err => reject(err));

    })

  }

  populateUomOptions(): void {
    this.extraCostParametersValues?.extraCosts!.forEach((extraCost: any) => {
      // Store the Metric and Imperial UOMs with combined keys
      this.uomOptionsMap[`${extraCost.extraCostName} Uom_Metric`] = extraCost.extraCostMetricUoms!.map((uom: any) => ({ value: uom, text: uom }));
      this.uomOptionsMap[`${extraCost.extraCostName} Uom_Imperial`] = extraCost.extraCostImperialUoms!.map((uom: any) => ({ value: uom, text: uom }));
    });
  }

  populateBooleanOptions(): void {
    this.booleanColumnsOptions = this.getBooleanColumnFilterOptions();
  }


  isExtraCostAmountColumn(col: string): boolean {
    return this.extraCostParametersValues!.extraCosts!.some((extraCost: any) => {
      return col === extraCost.extraCostName; // Check if the column matches any extra cost name
    });
  }

  isExtraCostUomColumn(col: string, item: any): boolean {
    return item.extraCostsCollection.some((extraCost: any) => {
      const ec = this.extraCostParametersValues!.extraCosts!.find(e => e.extraCostId === extraCost.extraCostId);
      return ec && col === `${ec.extraCostName} Uom`;
    });
  }

  extraCostMatchesColumn(col: string, extraCost: any, isUom: boolean = false): boolean {
    const ec = this.extraCostParametersValues!.extraCosts!.find(e => e.extraCostId === extraCost.extraCostId);
    if (!ec) return false;

    return isUom ? col === `${ec.extraCostName} Uom` : col === ec.extraCostName;
  }

  getUomOptionsBySystem(col: string, item: EnquiryHeaderItem): SelectOption[] {
    // Find the correct extra cost based on the column name in the preloaded extraCostParametersValues
    const baseCol = col.endsWith(' Uom') ? col.replace(' Uom', '') : col;

    // Find the correct extra cost based on the base column name (without Uom suffix)
    const ec = this.extraCostParametersValues!.extraCosts!.find(e => e.extraCostName === baseCol);

    if (ec) {
      // Return the appropriate UOM list based on the item's unit system
      const uomList = item.unitSystem === 'Metric' ? ec.extraCostMetricUoms : ec.extraCostImperialUoms;

      return uomList!.map(uom => ({ value: uom, text: uom }));
    }

    return [];
  }

  initGridConfiguration() {
    const userConfiguration = this.utils.getUserConfiguration();
    const savedConfig = userConfiguration.gridsConfigurations?.find(cfg => cfg.name === this.title);
    let configurationChanged = false;
    const currentColumnNames = this.getAllDisplayedColumns();

    if (savedConfig) {
      this.gridConfiguration = savedConfig;
      configurationChanged = this.sanitizeGridConfigurationColumns(this.gridConfiguration);

      const existingColumnNames = new Set(this.gridConfiguration.gridColumns!.map(c => c.name));

      // Add new columns that are missing from saved config
      const nextId = this.gridConfiguration.gridColumns!.length;
      currentColumnNames.forEach((col, idx) => {
        if (!existingColumnNames.has(col)) {
          let gc = new GridColumn();
          gc.id = nextId + idx;
          gc.name = col;
          gc.visible = true;
          gc.width = col === 'itemNo' ? 0 : this.DEFAULT_COL_WIDTH;
          gc.position = nextId + idx + 1;
          this.gridConfiguration.gridColumns!.push(gc);
          configurationChanged = true;
        }
      });

      // Remove columns that no longer exist
      const filteredColumns = this.gridConfiguration.gridColumns!.filter(c =>
        currentColumnNames.includes(c.name!)
      );

      if (filteredColumns.length !== this.gridConfiguration.gridColumns!.length) {
        this.gridConfiguration.gridColumns = filteredColumns;
        configurationChanged = true;
      }

    } else {
      // No saved config, initialize from scratch
      this.gridConfiguration = new GridConfiguration();
      this.gridConfiguration.name = this.title;
      this.gridConfiguration.gridColumns = currentColumnNames.map((col, idx) => {
        let gc = new GridColumn();
        gc.id = idx;
        gc.name = col;
        gc.visible = true;
        gc.width = col === 'itemNo' ? 0 : this.DEFAULT_COL_WIDTH;
        gc.position = idx + 1;
        return gc;
      });
      configurationChanged = true;
    }

    // Finally apply config
    if ((this.gridConfiguration.gridColumns?.length ?? 0) > 0) {
      this.applyGridConfiguration(this.gridConfiguration);
      const groupsChanged = this.syncItemColumnGroupsFromConfiguration();
      // Group toggles in headers depend on group state: rebuild caches after syncing groups.
      this.rebuildItemColumnCaches();
      if (groupsChanged) {
        this.persistItemColumnGroups();
        configurationChanged = true;
      }
    }

    if (configurationChanged) {
      this.onUpdateConfiguration(this.gridConfiguration);
    }
  }

  private getAllDisplayedColumns(): string[] {
    return this.dedupeColumns([
      ...this.columns.items,
      ...this.columns.extraCosts,
      ...this.columns.costs
    ]);
  }

  private sanitizeGridConfigurationColumns(gridConfiguration: GridConfiguration): boolean {
    let changed = false;
    if (!gridConfiguration.gridColumns) {
      gridConfiguration.gridColumns = [];
      return changed;
    }

    const seen = new Set<string>();
    const sanitizedColumns: GridColumn[] = [];

    gridConfiguration.gridColumns.forEach((column, index) => {
      const columnName = typeof column === 'string' ? column : column.name ?? '';

      if (!columnName) {
        changed = true;
        return;
      }

      const normalizedName = columnName.toLowerCase();
      if (seen.has(normalizedName)) {
        changed = true;
        return;
      }

      seen.add(normalizedName);

      const gridColumn = column instanceof GridColumn ? column : Object.assign(new GridColumn(), column);
      gridColumn.name = columnName;

      if (gridColumn.id === undefined || gridColumn.id === null) {
        gridColumn.id = index;
        changed = true;
      }

      sanitizedColumns.push(gridColumn);
    });

    sanitizedColumns.forEach((column, idx) => {
      if (column.position === undefined || column.position === null) {
        column.position = idx + 1;
        changed = true;
      }
    });

    if (sanitizedColumns.length !== gridConfiguration.gridColumns.length) {
      gridConfiguration.gridColumns = sanitizedColumns;
      changed = true;
    } else {
      gridConfiguration.gridColumns = sanitizedColumns;
    }

    return changed;
  }

  applyGridConfiguration(gridConfiguration: GridConfiguration) {
    if (this.availableItemColumnsLookup.size === 0) {
      this.rebuildAvailableItemColumnsCache();
    }
    const availableColumns = new Set<string>(this.availableItemColumnsOrdered);

    const visibleColumns = (gridConfiguration.gridColumns ?? [])
      .filter(gc => gc.visible && gc.name && availableColumns.has(gc.name))
      .map(gc => gc.name!);

    this.columns.items = this.dedupeColumns(visibleColumns);
    this.pruneItemColumnQuickSelection();
    this.rebuildItemColumnCaches();
    this.buildGroupsButtonsList();
    this.buildButtonsList();
  }

  canQuickHideItemColumn(columnName: string): boolean {
    if (!columnName) return false;
    if (columnName.toLowerCase() === 'itemno') return false;
    return this.columns.items.length > 1;
  }

  hasQuickShowItemColumnsBeforeVisible(columnName: string): boolean {
    return this.getHiddenItemColumnsBeforeVisible(columnName).length > 0;
  }

  getQuickShowItemColumnsCountBeforeVisible(columnName: string): number {
    return this.getHiddenItemColumnsBeforeVisible(columnName).length;
  }

  onQuickShowItemColumnsBeforeVisible(columnName: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showHiddenItemColumns(this.getHiddenItemColumnsBeforeVisible(columnName));
  }

  hasQuickShowItemColumnsBetweenVisible(columnName: string): boolean {
    return this.getHiddenItemColumnsBetweenVisible(columnName).length > 0;
  }

  getQuickShowItemColumnsCountBetweenVisible(columnName: string): number {
    return this.getHiddenItemColumnsBetweenVisible(columnName).length;
  }

  onQuickShowItemColumnsBetweenVisible(columnName: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showHiddenItemColumns(this.getHiddenItemColumnsBetweenVisible(columnName));
  }

  hasQuickShowItemColumnsAfterVisible(columnName: string): boolean {
    return this.getHiddenItemColumnsAfterVisible(columnName).length > 0;
  }

  getQuickShowItemColumnsCountAfterVisible(columnName: string): number {
    return this.getHiddenItemColumnsAfterVisible(columnName).length;
  }

  onQuickShowItemColumnsAfterVisible(columnName: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showHiddenItemColumns(this.getHiddenItemColumnsAfterVisible(columnName));
  }

  hasHeaderToggleBefore(columnName: string): boolean {
    const normalized = columnName.toLowerCase();
    return this.groupToggleBeforeByItemColumn.has(normalized) || this.hasQuickShowItemColumnsBeforeVisible(columnName);
  }

  hasHeaderToggleAfter(columnName: string): boolean {
    const normalized = columnName.toLowerCase();
    if (this.groupToggleAfterByItemColumn.has(normalized)) return true;
    return this.hasQuickShowItemColumnsBetweenVisible(columnName) || this.hasQuickShowItemColumnsAfterVisible(columnName);
  }

  getHeaderToggleBeforeIcon(columnName: string): string {
    const normalized = columnName.toLowerCase();
    const toggle = this.groupToggleBeforeByItemColumn.get(normalized);
    if (!toggle) return this.quickShowIconClass;
    return toggle.collapsed ? this.quickShowIconClass : 'fal fa-minus';
  }

  getHeaderToggleAfterIcon(columnName: string): string {
    const normalized = columnName.toLowerCase();
    const toggle = this.groupToggleAfterByItemColumn.get(normalized);
    if (!toggle) return this.quickShowIconClass;
    return toggle.collapsed ? this.quickShowIconClass : 'fal fa-minus';
  }

  getHeaderToggleBeforeTooltip(columnName: string): string {
    const normalized = columnName.toLowerCase();
    const toggle = this.groupToggleBeforeByItemColumn.get(normalized);
    if (!toggle) {
      return `Show hidden columns (${this.getQuickShowItemColumnsCountBeforeVisible(columnName)})`;
    }

    const localizedStart = this.getLocalizedGroupColumnName(toggle.startColumn);
    const localizedEnd = this.getLocalizedGroupColumnName(toggle.endColumn);
    if (toggle.collapsed) {
      return `Show ${localizedStart}-${localizedEnd}`;
    }
    return `Hide ${localizedStart}-${localizedEnd}`;
  }

  getHeaderToggleAfterTooltip(columnName: string): string {
    const normalized = columnName.toLowerCase();
    const toggle = this.groupToggleAfterByItemColumn.get(normalized);
    if (!toggle) {
      const betweenCount = this.getQuickShowItemColumnsCountBetweenVisible(columnName);
      if (betweenCount > 0) {
        return `Show hidden columns (${betweenCount})`;
      }
      return `Show hidden columns (${this.getQuickShowItemColumnsCountAfterVisible(columnName)})`;
    }

    const localizedStart = this.getLocalizedGroupColumnName(toggle.startColumn);
    const localizedEnd = this.getLocalizedGroupColumnName(toggle.endColumn);
    if (toggle.collapsed) {
      return `Show ${localizedStart}-${localizedEnd}`;
    }
    return `Hide ${localizedStart}-${localizedEnd}`;
  }

  onHeaderToggleBefore(columnName: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const normalized = columnName.toLowerCase();
    const toggle = this.groupToggleBeforeByItemColumn.get(normalized);
    if (toggle) {
      this.toggleItemColumnGroupVisibility(toggle.groupId);
      return;
    }

    this.showHiddenItemColumns(this.getHiddenItemColumnsBeforeVisible(columnName));
  }

  onHeaderToggleAfter(columnName: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const normalized = columnName.toLowerCase();
    const toggle = this.groupToggleAfterByItemColumn.get(normalized);
    if (toggle) {
      this.toggleItemColumnGroupVisibility(toggle.groupId);
      return;
    }

    const hiddenBetween = this.getHiddenItemColumnsBetweenVisible(columnName);
    if (hiddenBetween.length > 0) {
      this.showHiddenItemColumns(hiddenBetween);
      return;
    }

    this.showHiddenItemColumns(this.getHiddenItemColumnsAfterVisible(columnName));
  }

  private showHiddenItemColumns(hiddenColumns: string[]) {
    if (hiddenColumns.length === 0) return;

    hiddenColumns.forEach(columnName => {
      const gridColumn = this.findItemGridColumn(columnName);
      if (gridColumn) {
        gridColumn.visible = true;
      }
    });

    this.applyGridConfiguration(this.gridConfiguration);
    this.onUpdateConfiguration(this.gridConfiguration);
    this.refreshColumnsWidth();
  }

  isItemColumnQuickHideSelected(columnName: string): boolean {
    if (!columnName) return false;
    return this.selectedItemColumnsLookup.has(columnName.toLowerCase());
  }

  onItemColumnHeaderClick(columnName: string, event: MouseEvent): void {
    event.stopPropagation();

    if (!this.canQuickHideItemColumn(columnName)) {
      this.clearItemColumnQuickSelection();
      return;
    }

    const isShiftOrCmdPressed = event.shiftKey || event.metaKey;
    if (!isShiftOrCmdPressed) {
      this.clearItemColumnQuickSelection();
      this.setItemColumnQuickSelection([columnName], columnName);
      return;
    }

    if (!this.selectedItemColumnAnchor) {
      this.setItemColumnQuickSelection([columnName], columnName);
      return;
    }

    const orderedColumns = this.columns.items.filter(col => this.canQuickHideItemColumn(col));
    if (orderedColumns.length === 0) {
      this.clearItemColumnQuickSelection();
      return;
    }

    const normalizedAnchor = (this.selectedItemColumnAnchor ?? '').toLowerCase();
    const anchorColumn = orderedColumns.find(col => col.toLowerCase() === normalizedAnchor) ?? columnName;
    const anchorIndex = orderedColumns.findIndex(col => col.toLowerCase() === anchorColumn.toLowerCase());
    const targetIndex = orderedColumns.findIndex(col => col.toLowerCase() === columnName.toLowerCase());

    if (anchorIndex === -1 || targetIndex === -1) {
      this.setItemColumnQuickSelection([columnName], columnName);
      return;
    }

    const [start, end] = [anchorIndex, targetIndex].sort((a, b) => a - b);
    this.setItemColumnQuickSelection(orderedColumns.slice(start, end + 1), this.selectedItemColumnAnchor);
  }

  clearItemColumnQuickSelection(): void {
    if (this.selectedItemColumnsForQuickHide.length === 0 && !this.selectedItemColumnAnchor) return;
    this.selectedItemColumnsForQuickHide = [];
    this.selectedItemColumnsLookup.clear();
    this.selectedItemColumnAnchor = null;
    this.updateHideSelectedButtonState();
  }

  private setItemColumnQuickSelection(columns: string[], anchor: string | null): void {
    this.selectedItemColumnsForQuickHide = this.dedupeColumns(columns);
    this.selectedItemColumnsLookup = new Set(this.selectedItemColumnsForQuickHide.map(col => col.toLowerCase()));
    this.selectedItemColumnAnchor = anchor;
    this.updateHideSelectedButtonState();
  }

  private pruneItemColumnQuickSelection(): void {
    const visibleColumns = new Set(this.columns.items.map(col => col.toLowerCase()));
    this.selectedItemColumnsForQuickHide = this.selectedItemColumnsForQuickHide
      .filter(col => visibleColumns.has(col.toLowerCase()));
    this.selectedItemColumnsLookup = new Set(this.selectedItemColumnsForQuickHide.map(col => col.toLowerCase()));

    if (this.selectedItemColumnAnchor && !visibleColumns.has(this.selectedItemColumnAnchor.toLowerCase())) {
      this.selectedItemColumnAnchor = null;
    }

    if (!this.selectedItemColumnAnchor && this.selectedItemColumnsForQuickHide.length > 0) {
      this.selectedItemColumnAnchor = this.selectedItemColumnsForQuickHide[0];
    }

    this.updateHideSelectedButtonState();
  }

  getVisibleSelectedItemColumns(): string[] {
    const visibleColumns = new Set(this.columns.items.map(col => col.toLowerCase()));
    const selected = this.selectedItemColumnsForQuickHide
      .filter(col => visibleColumns.has(col.toLowerCase()));

    return this.dedupeColumns(selected);
  }

  getSelectedItemColumnCount(): number {
    return this.getVisibleSelectedItemColumns().length;
  }

  private hideSelectedItemColumnsAsGroup(): void {
    const selectedColumns = this.getVisibleSelectedItemColumns();
    if (selectedColumns.length < 1) return;

    const orderedColumns = this.getItemColumnsInOrder().filter(col => this.isGroupManagedItemColumn(col));
    const startName = selectedColumns[0];
    const endName = selectedColumns[selectedColumns.length - 1];
    const startIndex = orderedColumns.findIndex(col => col.toLowerCase() === startName.toLowerCase());
    const endIndex = orderedColumns.findIndex(col => col.toLowerCase() === endName.toLowerCase());
    if (startIndex === -1 || endIndex === -1) return;

    const [start, end] = [startIndex, endIndex].sort((a, b) => a - b);
    const columnsRange = orderedColumns.slice(start, end + 1);
    if (columnsRange.length < 1) return;

    const visibleColumns = this.columns.items.filter(col => this.canQuickHideItemColumn(col));
    const targetsCurrentlyVisible = columnsRange.filter(columnName => {
      const gridColumn = this.findItemGridColumn(columnName);
      return !!gridColumn && gridColumn.visible !== false;
    });

    if (visibleColumns.length - targetsCurrentlyVisible.length <= 0) {
      return;
    }

    targetsCurrentlyVisible.forEach(columnName => {
      const gridColumn = this.findItemGridColumn(columnName);
      if (gridColumn) {
        gridColumn.visible = false;
      }
    });

    this.upsertItemColumnGroup(columnsRange[0], columnsRange[columnsRange.length - 1]);
    this.persistItemColumnGroups();
    this.applyGridConfiguration(this.gridConfiguration);
    this.onUpdateConfiguration(this.gridConfiguration);
    this.refreshColumnsWidth();
    this.clearItemColumnQuickSelection();
  }

  private toggleItemColumnGroupVisibility(groupId: string): void {
    const group = this.itemColumnGroups.find(g => g.id === groupId);
    if (!group) return;

    const range = this.resolveGroupColumnsRange(group);
    if (range.length === 0) return;

    const collapsed = this.isItemColumnGroupCollapsed(group);
    if (collapsed) {
      range.forEach(columnName => {
        const gridColumn = this.findItemGridColumn(columnName);
        if (gridColumn) {
          gridColumn.visible = true;
        }
      });
    } else {
      const visibleColumns = this.columns.items.filter(col => this.canQuickHideItemColumn(col));
      const hideCandidates = range.filter(columnName => {
        const gridColumn = this.findItemGridColumn(columnName);
        return !!gridColumn && gridColumn.visible !== false;
      });
      if (visibleColumns.length - hideCandidates.length <= 0) {
        return;
      }

      hideCandidates.forEach(columnName => {
        const gridColumn = this.findItemGridColumn(columnName);
        if (gridColumn) {
          gridColumn.visible = false;
        }
      });
    }

    this.applyGridConfiguration(this.gridConfiguration);
    this.onUpdateConfiguration(this.gridConfiguration);
    this.refreshColumnsWidth();
    this.buildGroupsButtonsList();
  }

  private removeItemColumnGroup(groupId: string): void {
    const groupToRemove = this.itemColumnGroups.find(g => g.id === groupId);
    if (!groupToRemove) return;

    const range = this.resolveGroupColumnsRange(groupToRemove);
    range.forEach(columnName => {
      const gridColumn = this.findItemGridColumn(columnName);
      if (gridColumn) {
        gridColumn.visible = true;
      }
    });

    this.itemColumnGroups = this.itemColumnGroups.filter(g => g.id !== groupId);
    this.persistItemColumnGroups();
    this.applyGridConfiguration(this.gridConfiguration);
    this.buildGroupsButtonsList();
    this.refreshColumnsWidth();
    this.onUpdateConfiguration(this.gridConfiguration);
  }

  private upsertItemColumnGroup(startColumn: string, endColumn: string): void {
    const normalizedStart = startColumn.toLowerCase();
    const normalizedEnd = endColumn.toLowerCase();

    const existing = this.itemColumnGroups.find(group =>
      group.startColumn.toLowerCase() === normalizedStart &&
      group.endColumn.toLowerCase() === normalizedEnd
    );

    if (existing) return;

    const groupedRange = this.resolveGroupColumnsRange({ id: '', startColumn, endColumn });
    const groupedSet = new Set(groupedRange.map(col => col.toLowerCase()));

    this.itemColumnGroups = this.itemColumnGroups.filter(group => {
      const currentRange = this.resolveGroupColumnsRange(group);
      return !currentRange.some(col => groupedSet.has(col.toLowerCase()));
    });

    this.itemColumnGroups.push({
      id: `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      startColumn,
      endColumn
    });

    this.sortItemColumnGroups();
    this.buildGroupsButtonsList();
  }

  private sortItemColumnGroups(): void {
    const orderedColumns = this.getItemColumnsInOrder();
    this.itemColumnGroups = [...this.itemColumnGroups].sort((a, b) => {
      const aStart = orderedColumns.findIndex(col => col.toLowerCase() === a.startColumn.toLowerCase());
      const bStart = orderedColumns.findIndex(col => col.toLowerCase() === b.startColumn.toLowerCase());
      return aStart - bStart;
    });
  }

  private resolveGroupColumnsRange(group: ItemColumnGroup): string[] {
    const orderedColumns = this.getItemColumnsInOrder().filter(col => this.isGroupManagedItemColumn(col));
    const startIndex = orderedColumns.findIndex(col => col.toLowerCase() === group.startColumn.toLowerCase());
    const endIndex = orderedColumns.findIndex(col => col.toLowerCase() === group.endColumn.toLowerCase());
    if (startIndex === -1 || endIndex === -1) return [];

    const [start, end] = [startIndex, endIndex].sort((a, b) => a - b);
    return orderedColumns.slice(start, end + 1);
  }

  private getItemColumnGroupLabel(group: ItemColumnGroup): string {
    const range = this.resolveGroupColumnsRange(group);
    if (range.length === 0) return `${group.startColumn}-${group.endColumn}`;
    const start = this.getLocalizedGroupColumnName(range[0]);
    const end = this.getLocalizedGroupColumnName(range[range.length - 1]);
    const hiddenCount = range.filter(col => this.findItemGridColumn(col)?.visible === false).length;
    return `${start}..${end}${hiddenCount > 0 ? ` (${hiddenCount})` : ''}`;
  }

  private getLocalizedGroupColumnName(columnName: string): string {
    if (!columnName) return '';
    const translationKey = `grid.${this.capitalizeFirstLetter(columnName)}`;
    const translated = this.translateService.instant(translationKey);
    if (!translated || translated === translationKey) {
      return columnName;
    }
    return translated;
  }

  private isItemColumnGroupCollapsed(group: ItemColumnGroup): boolean {
    const range = this.resolveGroupColumnsRange(group);
    if (range.length === 0) return false;

    const visibility = range.map(columnName => this.findItemGridColumn(columnName))
      .filter(gc => !!gc)
      .map(gc => gc!.visible === false);

    return visibility.length > 0 && visibility.every(x => x);
  }

  private buildGroupsButtonsList(): void {
    this.groupsButtonsList = [];
    this.itemColumnGroups.forEach((group, idx) => {
      const item = new TTButton();
      item.id = idx + 1;
      item.disabled = false;
      item.icon = this.isItemColumnGroupCollapsed(group) ? 'fal fa-square-plus' : 'fal fa-square-minus';
      item.rawText = this.getItemColumnGroupLabel(group);
      const localizedStart = this.getLocalizedGroupColumnName(group.startColumn);
      const localizedEnd = this.getLocalizedGroupColumnName(group.endColumn);
      item.rawTooltip = this.isItemColumnGroupCollapsed(group)
        ? `Show ${localizedStart}-${localizedEnd}`
        : `Hide ${localizedStart}-${localizedEnd}`;
      item.action = `toggleGroup|${group.id}`;
      item.secondaryIcon = 'fal fa-trash-alt';
      item.secondaryRawTooltip = `Delete group ${localizedStart}-${localizedEnd}`;
      item.secondaryAction = `removeGroup|${group.id}`;
      item.type = '';
      item.state = this.isItemColumnGroupCollapsed(group) ? '' : 'pressed';
      this.groupsButtonsList.push(item);
    });
  }

  private syncItemColumnGroupsFromConfiguration(): boolean {
    const storedGroups = this.gridConfiguration.options?.[this.columnGroupingOptionsKey]?.groups;
    if (!Array.isArray(storedGroups)) {
      const hadGroups = this.itemColumnGroups.length > 0;
      this.itemColumnGroups = [];
      this.groupsButtonsList = [];
      return hadGroups;
    }

    const normalized: ItemColumnGroup[] = [];
    const occupied = new Set<string>();

    storedGroups.forEach((group: any, idx: number) => {
      const startColumn = typeof group?.startColumn === 'string' ? group.startColumn : '';
      const endColumn = typeof group?.endColumn === 'string' ? group.endColumn : '';
      if (!startColumn || !endColumn) return;

      const candidate: ItemColumnGroup = {
        id: typeof group?.id === 'string' && group.id !== '' ? group.id : `group_${idx + 1}`,
        startColumn,
        endColumn
      };

      const range = this.resolveGroupColumnsRange(candidate);
      if (range.length < 1) return;

      const overlap = range.some(col => occupied.has(col.toLowerCase()));
      if (overlap) return;

      range.forEach(col => occupied.add(col.toLowerCase()));
      normalized.push(candidate);
    });

    const previous = JSON.stringify(this.itemColumnGroups);
    this.itemColumnGroups = normalized;
    this.sortItemColumnGroups();
    this.buildGroupsButtonsList();
    return previous !== JSON.stringify(this.itemColumnGroups);
  }

  private persistItemColumnGroups(): void {
    const options = this.gridConfiguration.options ?? {};
    options[this.columnGroupingOptionsKey] = {
      groups: this.itemColumnGroups.map(group => ({
        id: group.id,
        startColumn: group.startColumn,
        endColumn: group.endColumn
      }))
    };
    this.gridConfiguration.options = options;
  }

  private findItemGridColumn(columnName: string): GridColumn | undefined {
    return (this.gridConfiguration.gridColumns ?? []).find(gc =>
      gc.name?.toLowerCase() === columnName.toLowerCase() && this.isItemColumn(gc.name!)
    );
  }

  private rebuildAvailableItemColumnsCache(): void {
    this.availableItemColumnsOrdered = this.getEffectiveItemVisibleColumns();
    this.availableItemColumnsLookup = new Set(this.availableItemColumnsOrdered.map(col => col.toLowerCase()));
  }

  private isItemColumn(columnName: string): boolean {
    if (!columnName) return false;
    if (this.availableItemColumnsLookup.size === 0) {
      this.rebuildAvailableItemColumnsCache();
    }
    return this.availableItemColumnsLookup.has(columnName.toLowerCase());
  }

  private isGroupManagedItemColumn(columnName: string): boolean {
    if (!columnName) return false;
    return columnName.toLowerCase() !== 'itemno' && this.isItemColumn(columnName);
  }

  private getItemColumnsInOrder(): string[] {
    if (this.availableItemColumnsLookup.size === 0) {
      this.rebuildAvailableItemColumnsCache();
    }
    const availableColumns = new Set<string>(this.availableItemColumnsOrdered);

    const orderedFromConfig = (this.gridConfiguration.gridColumns ?? [])
      .filter(gc => !!gc.name && availableColumns.has(gc.name!))
      .map(gc => gc.name!);

    // Preserve missing configured columns by appending them at the end.
    const missing = [...availableColumns].filter(col => !orderedFromConfig.includes(col));
    return this.dedupeColumns([...orderedFromConfig, ...missing]);
  }

  private getHiddenItemColumnsBetweenVisible(columnName: string): string[] {
    return this.quickShowBetweenColumnsByItemColumn.get(columnName.toLowerCase()) ?? [];
  }

  private getHiddenItemColumnsBeforeVisible(columnName: string): string[] {
    return this.quickShowBeforeColumnsByItemColumn.get(columnName.toLowerCase()) ?? [];
  }

  private getHiddenItemColumnsAfterVisible(columnName: string): string[] {
    return this.quickShowAfterColumnsByItemColumn.get(columnName.toLowerCase()) ?? [];
  }

  private rebuildItemColumnCaches(): void {
    const orderedColumns = this.getItemColumnsInOrder();
    const orderedByNormalized = new Map<string, number>();
    orderedColumns.forEach((columnName, index) => orderedByNormalized.set(columnName.toLowerCase(), index));

    const gridColumnsByName = new Map<string, GridColumn>();
    this.columnWidthByName = new Map<string, number>();
    (this.gridConfiguration.gridColumns ?? []).forEach(gc => {
      if (!gc.name) return;
      const normalized = gc.name.toLowerCase();
      gridColumnsByName.set(normalized, gc);
      this.columnWidthByName.set(normalized, gc.width ?? this.DEFAULT_COL_WIDTH);
    });

    const visibilityByName = new Map<string, boolean>();
    orderedColumns.forEach(columnName => {
      const normalized = columnName.toLowerCase();
      const column = gridColumnsByName.get(normalized);
      visibilityByName.set(normalized, column ? column.visible !== false : true);
    });

    this.itemHeaderLabelByColumn = new Map<string, string>();
    this.quickShowBeforeColumnsByItemColumn = new Map<string, string[]>();
    this.quickShowBetweenColumnsByItemColumn = new Map<string, string[]>();
    this.quickShowAfterColumnsByItemColumn = new Map<string, string[]>();
    this.groupToggleBeforeByItemColumn = new Map<string, ItemColumnGroupHeaderToggle>();
    this.groupToggleAfterByItemColumn = new Map<string, ItemColumnGroupHeaderToggle>();

    this.columns.items.forEach(columnName => {
      const normalized = columnName.toLowerCase();
      this.itemHeaderLabelByColumn.set(normalized, this.getColumnHeaderLabel(columnName));

      const currentIndex = orderedByNormalized.get(normalized);
      if (currentIndex === undefined || currentIndex === -1) {
        this.quickShowBeforeColumnsByItemColumn.set(normalized, []);
        this.quickShowBetweenColumnsByItemColumn.set(normalized, []);
        this.quickShowAfterColumnsByItemColumn.set(normalized, []);
        return;
      }

      const before: string[] = [];
      let beforeBlocked = false;
      for (let i = currentIndex - 1; i >= 0; i--) {
        const prevColumn = orderedColumns[i];
        const prevVisible = visibilityByName.get(prevColumn.toLowerCase()) !== false;
        if (!prevVisible) {
          before.push(prevColumn);
          continue;
        }
        beforeBlocked = true;
        break;
      }
      this.quickShowBeforeColumnsByItemColumn.set(normalized, beforeBlocked ? [] : before);

      const between: string[] = [];
      for (let i = currentIndex + 1; i < orderedColumns.length; i++) {
        const nextColumn = orderedColumns[i];
        const nextVisible = visibilityByName.get(nextColumn.toLowerCase()) !== false;
        if (!nextVisible) {
          between.push(nextColumn);
          continue;
        }
        break;
      }
      this.quickShowBetweenColumnsByItemColumn.set(normalized, between);

      const after: string[] = [];
      let afterBlocked = false;
      for (let i = currentIndex + 1; i < orderedColumns.length; i++) {
        const nextColumn = orderedColumns[i];
        const nextVisible = visibilityByName.get(nextColumn.toLowerCase()) !== false;
        if (!nextVisible) {
          after.push(nextColumn);
          continue;
        }
        afterBlocked = true;
        break;
      }
      this.quickShowAfterColumnsByItemColumn.set(normalized, afterBlocked ? [] : after);
    });

    const groupManagedOrderedColumns = orderedColumns.filter(col => this.isGroupManagedItemColumn(col));
    const groupManagedIndexByName = new Map<string, number>();
    groupManagedOrderedColumns.forEach((columnName, index) => {
      groupManagedIndexByName.set(columnName.toLowerCase(), index);
    });

    this.itemColumnGroups.forEach(group => {
      const range = this.resolveGroupColumnsRange(group);
      if (range.length === 0) return;

      const startColumn = range[0];
      const endColumn = range[range.length - 1];
      const startIndex = groupManagedIndexByName.get(startColumn.toLowerCase());
      const endIndex = groupManagedIndexByName.get(endColumn.toLowerCase());
      if (startIndex === undefined || endIndex === undefined) return;

      const visibleRangeColumns = range.filter(col => visibilityByName.get(col.toLowerCase()) !== false);
      const hiddenCount = range.length - visibleRangeColumns.length;
      const collapsed = hiddenCount === range.length;

      const toggle: ItemColumnGroupHeaderToggle = {
        groupId: group.id,
        collapsed,
        hiddenCount,
        startColumn: group.startColumn,
        endColumn: group.endColumn
      };

      let leftVisibleAnchor: string | null = null;
      for (let i = startIndex - 1; i >= 0; i--) {
        const candidate = groupManagedOrderedColumns[i];
        if (visibilityByName.get(candidate.toLowerCase()) !== false) {
          leftVisibleAnchor = candidate;
          break;
        }
      }

      if (leftVisibleAnchor) {
        this.groupToggleAfterByItemColumn.set(leftVisibleAnchor.toLowerCase(), toggle);
        return;
      }

      let rightVisibleAnchor: string | null = null;
      for (let i = endIndex + 1; i < groupManagedOrderedColumns.length; i++) {
        const candidate = groupManagedOrderedColumns[i];
        if (visibilityByName.get(candidate.toLowerCase()) !== false) {
          rightVisibleAnchor = candidate;
          break;
        }
      }

      if (rightVisibleAnchor) {
        this.groupToggleBeforeByItemColumn.set(rightVisibleAnchor.toLowerCase(), toggle);
        return;
      }

      // Fallback for edge cases where the group is the only visible section.
      if (visibleRangeColumns.length > 0) {
        this.groupToggleAfterByItemColumn.set(visibleRangeColumns[0].toLowerCase(), toggle);
      }
    });

    this.frozenItemColumnsLookup = new Set<string>();
    this.stickyLeftByColumn = new Map<string, number>();
    const frozenVisibleColumns = this.getFrozenVisibleItemColumns();
    let cumulativeLeft = this.checkboxColumnContainerWidth;
    frozenVisibleColumns.forEach(column => {
      const normalized = (column.name ?? '').toLowerCase();
      if (!normalized) return;
      this.frozenItemColumnsLookup.add(normalized);
      this.stickyLeftByColumn.set(normalized, cumulativeLeft);
      cumulativeLeft += column.width ?? this.DEFAULT_COL_WIDTH;
    });
  }

  showSettings() {
    this.showSettingsDialog = true;
  }

  onCloseSettings(evt: any) {
    this.showSettingsDialog = false;
  }

  onApplySettings(gridConfiguration: GridConfiguration) {
    this.resizableColumnsService.reset();
    this.gridConfiguration = new GridConfiguration()
    this.gridConfiguration = gridConfiguration;
    this.applyGridConfiguration(this.gridConfiguration);
    const groupsChanged = this.syncItemColumnGroupsFromConfiguration();
    // Keep header +/- toggles aligned with freshly synced grouping state.
    this.rebuildItemColumnCaches();
    if (groupsChanged) {
      this.persistItemColumnGroups();
    }
    this.onUpdateConfiguration(this.gridConfiguration);
    this.showSettingsDialog = false;
    this.refreshColumnsWidth();
    this.clearItemColumnQuickSelection();
  }

  refreshColumnsWidth() {
    this.recalculateColumnsWidth = true;
    setTimeout(() => {
      this.recalculateColumnsWidth = false;
    }, 0);
  }

  onUpdateConfiguration(gridConfiguration: GridConfiguration) {
    this.utils.upsertGridConfiguration(gridConfiguration);
    this.updateUserConfiguration();
  }

  updateUserConfiguration() {
    this.backendService.updateUserConfiguration(this.globals.userLogged!.configuration!, this.globals.userLogged!.language!, this.globals.userLogged!.dateFormat!).subscribe(() => {
      localStorage.setItem("userLogged", JSON.stringify(this.globals.userLogged!));
    });
  }

  hasValidationIssue(item: EnquiryHeaderItem, column: string): boolean {
    if (column === 'extraLeadTime') {
      return this.hasInvalidResultingDeliveryWeek(item);
    }

    const itm = this.itemsPricesFromXlsx?.items?.find(v => v.item?.itemNo === item.itemNo);
    if (itm && itm.validations) {
      if ((column === 'actualFm' || column === 'actualFmAmountPerWeight') && itm.validations.actualMarginValidation) {
        return !itm.validations.actualMarginValidation.isValid;
      }
      if (column === 'finalPrice' && itm.validations.finalPriceValidation) {
        return !itm.validations.finalPriceValidation.isValid;
      }
      if (column === 'negotiationMargin' && itm.validations.negotiationMarginValidation) {
        return !itm.validations.negotiationMarginValidation.isValid;
      }
      if (column === 'agentFee' && itm.validations.agentFeeValidation) {
        return !itm.validations.agentFeeValidation.isValid;
      }
    }
    return false;
  }

  hasValidationIssueExtraCost(item: EnquiryHeaderItem, extraCostColumn: string) {
    const itm = this.itemsPricesFromXlsx?.items?.find(v => v.item?.itemNo === item.itemNo)!;
    if (!itm)
      return false;

    if (extraCostColumn.endsWith(' Uom')) {
      extraCostColumn = extraCostColumn.replace(' Uom', '')
    }
    const extraCostId = this.getExtraCostIdByName(extraCostColumn);
    const extraCostValidation = itm.validations?.extraCostsValidation![extraCostId!];
    return extraCostValidation ? !extraCostValidation.isValid : false;
  }

  getValidationMessageExtraCost(item: EnquiryHeaderItem, extraCostColumn: string): string {
    const itm = this.itemsPricesFromXlsx?.items?.find(v => v.item?.itemNo === item.itemNo);
    if (itm && itm.validations) {

      if (extraCostColumn.endsWith(' Uom')) {
        extraCostColumn = extraCostColumn.replace(' Uom', '')
      }
      const extraCostId = this.getExtraCostIdByName(extraCostColumn);
      const extraCostValidation = itm.validations?.extraCostsValidation![extraCostId!];
      return extraCostValidation?.message || '';
    }
    return '';
  }

  getValidationMessage(item: EnquiryHeaderItem, column: string): string {
    if (column === 'extraLeadTime' && this.hasInvalidResultingDeliveryWeek(item)) {
      return this.translateService.instant('enquiryBook.negativeDeliveryWeeksError');
    }

    const itm = this.itemsPricesFromXlsx?.items?.find(v => v.item?.itemNo === item.itemNo);
    if (itm && itm.validations) {
      if ((column === 'actualFm' || column === 'actualFmAmountPerWeight') && itm.validations.actualMarginValidation) {
        return itm.validations.actualMarginValidation.message || '';
      }
      if (column === 'finalPrice' && itm.validations.finalPriceValidation) {
        return itm.validations.finalPriceValidation.message || '';
      }
      if (column === 'negotiationMargin' && itm.validations.negotiationMarginValidation) {
        return itm.validations.negotiationMarginValidation.message || '';
      }
      if (column === 'agentFee' && itm.validations.agentFeeValidation) {
        return itm.validations.agentFeeValidation.message || '';
      }
    }
    return '';
  }

  removeValidationIssue(item: EnquiryHeaderItem, column: string, value: any): void {
    const itm = this.itemsPricesFromXlsx?.items?.find(v => v.item?.itemNo === item.itemNo);

    if (itm && itm.validations) {
      if ((column === 'actualFm' || column === 'actualFmAmountPerWeight') && itm.validations.actualMarginValidation) {
        itm.validations.actualMarginValidation.isValid = true; // all values are fine here
        if (itm.validations.actualMarginValidation.isValid) {
          itm.validations.actualMarginValidation.message = '';
        }
      }
      if (column === 'finalPrice' && itm.validations.finalPriceValidation) {
        itm.validations.finalPriceValidation.isValid = value >= 0;
        if (itm.validations.finalPriceValidation.isValid) {
          itm.validations.finalPriceValidation.message = '';
        }
      }
      if (column === 'negotiationMargin' && itm.validations.negotiationMarginValidation) {
        itm.validations.negotiationMarginValidation.isValid = value >= 0;
        if (itm.validations.negotiationMarginValidation.isValid) {
          itm.validations.negotiationMarginValidation.message = '';
        }
      }
      if (column === 'agentFee' && itm.validations.agentFeeValidation) {
        itm.validations.agentFeeValidation.isValid = value >= 0;
        if (itm.validations.agentFeeValidation.isValid) {
          itm.validations.agentFeeValidation.message = '';
        }
      }
      if (column === 'extraLeadTime' && itm.validations.extraLeadTimeValidation) {
        itm.validations.extraLeadTimeValidation.isValid = !this.hasInvalidResultingDeliveryWeek(item);
        if (itm.validations.extraLeadTimeValidation.isValid) {
          itm.validations.extraLeadTimeValidation.message = '';
        } else {
          itm.validations.extraLeadTimeValidation.message = this.translateService.instant('enquiryBook.negativeDeliveryWeeksError');
        }
      }
    }
    this.updateValidationStatus();
  }

  removeValidationIssueExtraCost(item: EnquiryHeaderItem, extraCostColumn: string, value: any) {
    const itm = this.itemsPricesFromXlsx?.items?.find(v => v.item?.itemNo === item.itemNo)!;

    if (itm) {
      const extraCostId = this.getExtraCostIdByName(extraCostColumn);
      const extraCostValidation = itm.validations?.extraCostsValidation![extraCostId!];
      if (extraCostValidation) {
        extraCostValidation.isValid = true;
        if (extraCostValidation.isValid) {
          extraCostValidation.message = '';
        }
      }
      this.updateValidationStatus();
    }

  }

  updateValidationStatus(): void {
    this.hasValidationIssues = this.itemsPricesFromXlsx?.items?.some(i =>
      (i.validations?.actualMarginValidation && !i.validations.actualMarginValidation.isValid) ||
      (i.validations?.finalPriceValidation && !i.validations.finalPriceValidation.isValid) ||
      (i.validations?.negotiationMarginValidation && !i.validations.negotiationMarginValidation.isValid) ||
      (i.validations?.agentFeeValidation && !i.validations.agentFeeValidation.isValid) ||
      (i.validations?.extraCostsValidation && Object.values(i.validations.extraCostsValidation).some(ec => !ec.isValid))
    ) || false;

    if (!this.hasValidationIssues) {
      this.hasValidationIssues = this.items.some(item => this.hasInvalidResultingDeliveryWeek(item));
    }
  }

  private hasInvalidResultingDeliveryWeek(item?: EnquiryHeaderItem | null): boolean {
    if (!item) {
      return false;
    }

    const deliverySource = (item.deliverySource || '').trim().toLowerCase();
    if (deliverySource === 'itdcr') {
      return false;
    }

    const deliveryDates = Array.isArray(item.deliveryDatesCollection) ? item.deliveryDatesCollection : [];
    return deliveryDates.some(dd => Number(dd.deliveryNumberOfWeeksWithExtraLeadTime) < 1);
  }

  getExtraCostIdByName(extraCostName: string): number | undefined {
    return this.extraCostParametersValues?.extraCosts?.find(ex => ex.extraCostName == extraCostName)?.extraCostId
  }

  priceClass(): string {
    return this.enquiryTotalMarginClass;
  }

  getTotalPrice(): string {
    return this.enquiryTotalPriceFormatted;
  }

  getTotalMargin(): string {
    return this.enquiryTotalMarginFormatted;
    let totalPriceEur = 0;     // Σ (pricePerKg * weightKg)
    let totalFmEur = 0;        // Σ (fmPerKg * weightKg)
    let totalNetBaseEur = 0;   // Σ (netBasePerKg * weightKg)

    if (this.items.length == 0 || !this.items.every(i => i.finalPrice))
      return '';

    for (const item of this.items) {
      const weightKg = Number(item['totalWeightStd']) || 0;
      if (!weightKg) continue;
      const finalPrice = Number(item.finalPrice) || 0;

      // Agent & bonus
      const agentFeeFrac = (Number(item.agentFee) || 0) / 100;
      const bonusFrac = (Number(item.customerBonus) || 0) / 100;

      const transportAmount = item.extraCostsCollection?.find(
        (ec: any) => ec.extraCostName === 'Transport Extra Cost'
      )?.extraCostAmount || 0;

      const oneMinusAgentAndBonus = 1 - agentFeeFrac - bonusFrac;
      const netBasePerKg = (finalPrice * oneMinusAgentAndBonus) - transportAmount;

      const fmFrac = (Number(item.actualFm) || 0) / 100;
      const fmPerKg = fmFrac * netBasePerKg;

      // Accumulate totals
      totalPriceEur += finalPrice * weightKg;
      totalFmEur += fmPerKg * weightKg;
      totalNetBaseEur += netBasePerKg * weightKg;
    }

    if (!totalNetBaseEur) return '0,00% / 0,00 ' + (this.items[0]?.['totalPriceUom'] ?? '');

    const totalFMPercentage = (totalFmEur / totalNetBaseEur) * 100;

    // fr-FR formatting (space thousands, comma decimals)
    const pct = totalFMPercentage.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
    const amt = totalFmEur.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + (this.items[0]['totalPriceUom'] ?? '');

    return `${pct} / ${amt}`;
  }

  private refreshEnquiryTotals(): Promise<void> {
    if (!this.enquiryNo) {
      this.resetEnquiryTotals();
      return Promise.resolve();
    }

    const requestId = ++this.enquiryTotalsRequestId;
    const payload = this.buildEnquiryTotalsPayload();

    return new Promise((resolve) => {
      this.subscriptions.add(this.backendService.getEnquiryTotalPriceAndMargin(this.enquiryNo, {
        usePayloadItems: true,
        payload
      }).subscribe({
        next: res => {
          if (requestId === this.enquiryTotalsRequestId) {
            this.enquiryTotals = res;
            this.applyEnquiryTotalsFormatting();
          }
          resolve();
        },
        error: () => {
          if (requestId === this.enquiryTotalsRequestId) {
            this.resetEnquiryTotals();
          }
          resolve();
        }
      }));
    });
  }

  private buildEnquiryTotalsPayload(): EnquiryTotalPriceAndMarginPayload {
    return {
      items: this.items.map(item => ({
        itemNo: item.itemNo ?? '',
        totalWeightStd: this.toPayloadNumber(item['totalWeightStd']),
        fullCost: this.toPayloadNumber(item.fullCost),
        actualFm: this.toPayloadNumber(item.actualFm),
        extraCostExcludingTransport: this.getExtraCostExcludingTransport(item),
        finalPrice: this.toPayloadNumber(item.finalPrice)
      }))
    };
  }

  private getExtraCostExcludingTransport(item: EnquiryHeaderItem): number {
    const extraCosts = this.toPayloadNumber(item.extraCosts);
    const transportExtraCostId = this.extraCostParametersValues?.extraCosts
      ?.find(extraCost => extraCost.extraCostName === 'Transport Extra Cost')
      ?.extraCostId;

    if (!transportExtraCostId) {
      return extraCosts;
    }

    const transportExtraCost = (item.extraCostsCollection || [])
      .filter(extraCost => extraCost?.extraCostId === transportExtraCostId)
      .reduce((total, extraCost) => total + this.toPayloadNumber(extraCost?.extraCostAmount), 0);

    return Number((extraCosts - transportExtraCost).toFixed(2));
  }

  private toPayloadNumber(value: any): number {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return 0;
    return Number(parsedValue.toFixed(2));
  }

  private applyEnquiryTotalsFormatting(): void {
    if (!this.enquiryTotals) {
      this.resetEnquiryTotals();
      return;
    }

    const totalPrice = this.parseNumber(this.enquiryTotals.totalPrice);
    const totalMargin = this.parseNumber(this.enquiryTotals.totalMargin);
    const totalMarginPercentage = this.parseNumber(this.enquiryTotals.totalMarginPercentage);
    if (totalPrice == null || totalMargin == null || totalMarginPercentage == null) {
      this.resetEnquiryTotals();
      return;
    }

    const priceUom = this.getTotalsPriceUom();
    const priceSuffix = priceUom ? ` ${priceUom}` : '';
    this.enquiryTotalPriceFormatted = `${this.formatNumber(totalPrice)}${priceSuffix}`;
    this.enquiryTotalMarginFormatted = `${this.formatNumber(totalMarginPercentage)}% / ${this.formatNumber(totalMargin)}${priceSuffix}`;
    this.enquiryTotalMarginClass = this.getMarginClass(totalMargin);
  }

  private resetEnquiryTotals(): void {
    this.enquiryTotals = null;
    this.enquiryTotalPriceFormatted = '';
    this.enquiryTotalMarginFormatted = '';
    this.enquiryTotalMarginClass = '';
  }

  private getTotalsPriceUom(): string {
    return this.items.find(item => !!item['totalPriceUom'])?.['totalPriceUom'] ?? '';
  }

  private parseNumber(value: string | number | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsedValue = typeof value === 'number'
      ? value
      : Number(value.toString().replace(/\s/g, '').replace(',', '.'));

    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private formatNumber(value: number): string {
    return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private getMarginClass(totalMargin: number): string {
    if (totalMargin > 0) return 'green-text';
    if (totalMargin < 0) return 'red-text';
    return '';
  }

  onColumnWidthUpdate(gridConfiguration: GridConfiguration) {
    // gridConfiguration.gridColumns = gridConfiguration.gridColumns?.filter(gc => this.columns.items.includes(gc.name!));
    this.gridConfiguration = gridConfiguration;
    this.rebuildItemColumnCaches();
    // let itemColumnsConfig = cloneDeep(gridConfiguration);
    // itemColumnsConfig.gridColumns = itemColumnsConfig.gridColumns?.filter(gc => this.columns.items.includes(gc.name!));
    this.onUpdateConfiguration(gridConfiguration);
  }

  onColumnResizeEnd() {
    // this.teiasoftLoader.start();
    // this.resizableColumnsService.columnResized();
  }



  setFocusedColumn(column: string): void {
    // TODO: handle extra costs column ? keep in mind that their name does not match an item property
    if (this.notEditableColumns.includes(column)) return;
    this.focusedColumn = column;
  }

  canIncreaseColumn(column: string): boolean {
    if (!column) return false;
    if (this.notEditableColumns.includes(column)) return false;
    return column.toLowerCase() !== 'deliverynotes' && column.toLowerCase() !== 'extraleadtime';
  }

  onIncreaseColumnClick(column: string): void {
    if (!this.canIncreaseColumn(column)) return;
    if (this.getSelectedItems().length < 1) {
      // TODO: maybe here show a message to suggest selecting a row
      return;
    }

    // if (this.focusedColumn) {
    this.focusedColumn = column;
    this.showIncreaseModal = true;
    // }
  }

  applyIncrease(event: { value: number; unit: 'percentage' | 'value' }): void {
    const { value, unit } = event;

    const updatesPromises = this.getSelectedItems().map((item: any) => {
      const currentValue = Number(item[this.focusedColumn!]);
      let newValue =
        unit === 'percentage'
          ? currentValue * (1 + value / 100)
          : currentValue + value;

      newValue = Math.round(newValue * 100) / 100

      return this.updateItemColumn(item, this.focusedColumn!, newValue);
    });


    Promise.all(updatesPromises).then(() => {
      this.calculateExtraCostSummary(this.getSelectedItems()).then(async () => {
        await this.refreshEnquiryTotals();
        this.showIncreaseModal = false;
        this.temporarilyUnselectItems();
      })
    });

  }

  trackByItem(index: number, item: any): any {
    return item.itemNo || index;
  }

  trackByColumn(index: number, column: string): any {
    return column || index;
  }

  getColumnWidth(column: string) {
    return this.columnWidthByName.get(column.toLowerCase()) ?? this.DEFAULT_COL_WIDTH;
  }

  private normalizeColumnName(columnName?: string): string {
    return (columnName ?? '').toLowerCase();
  }

  private getFrozenColumnsBoundary(): number {
    const configuredFrozen = this.gridConfiguration.frozenColumns ?? 0;
    if (configuredFrozen > 0) return configuredFrozen;
    return this.gridConfiguration.activeFrozenColumns ?? 0;
  }

  private getOrderedItemConfigColumns(): { column: GridColumn; order: number }[] {
    return (this.gridConfiguration.gridColumns ?? [])
      .map((column, index) => ({ column, order: column.position ?? (index + 1) }))
      .filter(item => !!item.column.name && this.isItemColumn(item.column.name!))
      .sort((a, b) => a.order - b.order);
  }

  private getFrozenVisibleItemColumns(): GridColumn[] {
    const frozenBoundary = this.getFrozenColumnsBoundary();
    if (frozenBoundary <= 0) return [];

    return this.getOrderedItemConfigColumns()
      .filter(item => item.order <= frozenBoundary && item.column.visible !== false)
      .map(item => item.column);
  }

  isFrozenItemColumn(columnName: string): boolean {
    if (!columnName) return false;
    return this.frozenItemColumnsLookup.has(this.normalizeColumnName(columnName));
  }

  getStickyLeftPosition(columnName: string): number | undefined {
    if (!columnName) {
      return undefined;
    }
    return this.stickyLeftByColumn.get(this.normalizeColumnName(columnName));
  }

  onFilterInputChange(evt: any, column: string) {
    if (this.getFilterType(column) === 'number') {
      let _temp: string[] = evt.split("|");

      if (_temp.length === 3) {
        const operator = _temp[0];
        const value = parseFloat(_temp[1]);

        if (!isNaN(value)) {
          this.activeFilters[column] = { operator, value };
        } else {
          delete this.activeFilters[column];
        }
      } else if (_temp.length === 4 && _temp[0] === 'ltgt') {
        const min = parseFloat(_temp[1]);
        const max = parseFloat(_temp[2]);

        if (!isNaN(min) && !isNaN(max)) {
          this.activeFilters[column] = { operator: 'ltgt', min, max };
        } else {
          delete this.activeFilters[column];
        }
      } else {
        delete this.activeFilters[column]; // Reset filter if invalid input
      }
    } else {
      // Handle text-based filters
      let value: string = evt;
      if (value !== '') {
        this.activeFilters[column] = value.toLowerCase();
      } else {
        delete this.activeFilters[column];
      }
    }
    this.applyFilters();
  }


  applyFilters() {
    this.filteredItems = this.items.filter(item => {
      return (
        Object.keys(this.activeFilters).every(key => {
          const filter = this.activeFilters[key];
          const itemValue = this.getInputValue(item, key);

          if (typeof filter === 'string') {
            // Text filtering (case-insensitive contains check)
            return itemValue.toString().toLowerCase().includes(filter);
          }

          if (typeof filter === 'object' && this.getFilterType(key) === 'number') {
            const value = parseFloat('' + itemValue);
            if (isNaN(value)) return false; // Ignore if value is not a number

            switch (filter.operator) {
              case 'gt': return value > filter.value;
              case 'lt': return value < filter.value;
              case 'eq': return value === filter.value;
              case 'ltgt': return value >= filter.min && value <= filter.max;
              default: return true;
            }
          }

          return true; // Default case 
        }) && (
          Object.keys(this.activeMultiSelectionFilters).every(key => {
            const selectedValues = this.activeMultiSelectionFilters[key];
            const itemValue = this.getInputValue(item, key);

            // If no selection made for this column, do not filter it
            if (!selectedValues || selectedValues.length === 0) return true;

            // If the column value exists in the selected filter options
            return selectedValues.includes(itemValue);
          })
        )
      );

    })
    this.lastSelectedIndex = null; // visible list changed: old shift-select anchor no longer valid
    this.clearSelectionForFilteredOutItems();
    this.buildButtonsList(); // update filter button status
    this.refreshColumnsWidth();
  }

  clearSelectionForFilteredOutItems(): void {
    const visibleItems = new Set(this.filteredItems);
    this.items.forEach(item => {
      if (!visibleItems.has(item) && item['isSelected']) {
        item['isSelected'] = false;
        item['isEditing'] = false;
      }
    });
  }

  trackByCol(index: number, col: string): string {
    return col;
  }

  getFilterType(column: string): 'number' | 'text' | 'select' | 'multi-select' | 'boolean' {
    if (this.numericColumns.includes(column))
      return 'number';
    if (this.selectColumns.includes(column))
      return 'select';
    if (this.multiSelectColumns.includes(column))
      return 'multi-select';
    if (this.booleanColumns.includes(column))
      return 'boolean'
    if (this.columns.extraCosts.includes(column) && !column.includes('Uom')) {
      return 'number';
    }
    return 'text';
  }

  getFilterValue(column: string): any {
    const type = this.getFilterType(column);
    let value;

    if (!this.activeFilters[column] && !(this.activeMultiSelectionFilters[column]))
      return '';

    if (type == 'number') {
      value = this.activeFilters[column].value;
    }
    else {
      value = this.activeFilters[column];
    }

    return value;
  }

  getSelectDefaultValue(col: string): any {
    return (this.activeMultiSelectionFilters?.[col] ?? [])[0];
  }

  isOrderExtraCost(extraCost: ExtraCost): boolean {
    const ec = this.extraCostParametersValues?.extraCosts?.find(ex => ex.extraCostId == extraCost.extraCostId);
    return !!(ec && ec.isOrderExtraCost);
  }

  getColumnFilterOptions(column: string, addEmptyOption: boolean = false): SelectOption[] {
    let values = this.items.map(i => this.getInputValue(i, column));
    let uniqueValues = [...new Set(values)];

    let options = uniqueValues.map(opt => {
      const sc = new SelectOption();
      sc.text = opt;
      sc.value = opt;
      return sc;
    })

    return options;
  }

  getBooleanColumnFilterOptions(): SelectOption[] {
    let values = ['', 'Yes', 'No'].map(opt => {
      const sc = new SelectOption();
      sc.text = opt;
      sc.value = opt;
      return sc;
    })

    return values;
  }

  onSelectedFilterOptions(selectedOptions: any[], column: string) {
    if (selectedOptions && selectedOptions.length == 1 && !selectedOptions[0]) {
      // if the selected option is undefined and no other options where selected we can remove it from filters
      delete this.activeMultiSelectionFilters[column];
    }
    else {
      this.activeMultiSelectionFilters[column] = selectedOptions;
    }
    this.applyFilters();
  }


  hasMismatch(row: EnquiryHeaderItem, column: string): boolean {

    // check column and relative ct value 
    if (row[column] != undefined && row[column + 'Ct'] != undefined) {

      // if they are defined 
      const toBooleanLike = (input: any): any => {
        if (typeof input === 'boolean') {
          return input;
        }
        if (typeof input === 'number') {
          if (input === 1) return true;
          if (input === 0) return false;
        }
        if (typeof input === 'string') {
          const normalized = input.trim().toLowerCase();
          if (['true', 'yes', 'y', '1'].includes(normalized)) return true;
          if (['false', 'no', 'n', '0'].includes(normalized)) return false;
        }
        return input;
      };

      const value = toBooleanLike(row[column]);
      const ctValue = toBooleanLike(row[column + 'Ct']);
      const ctValueMissing = row[column + 'Ct'] == null || (typeof row[column + 'Ct'] === 'string' && row[column + 'Ct'].trim() === '');
      if (ctValueMissing) return false; // MP: when ct column has no value we don't show any mismatch because they cannot be compared

      const matching = value === ctValue;
      const item = this.items?.find(i => i.itemNo == row.itemNo);
      const extraCost = item?.extraCostsCollection?.find(ex => ex.extraCostName == this.itemFieldsExtraCostsMap[column]);


      if (!matching) {
        if ((!extraCost || extraCost.extraCostAmount == 0)) {
          return true;
        }
      }
      if (matching) {
        // extra cost should be null or 0
        if (extraCost && extraCost.extraCostAmount != 0) {
          return true;
        }
      }

    }

    return false;
  }
}
